import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Icon,
  CardV2,
  Button,
  ButtonIcon,
  Feedback,
  ToastContainer,
} from "pepsico-ds";
import { appConstants } from "../../constants/appconstants";
import { getAuthHeader } from "../../utils/auth/unified-auth";
import { SpendAnalyticsSessionContext } from "./SpendAnalyticsSessionContext";
import SpendAnalyticsBadge from "./SpendAnalyticsBadge";
import "./MarketIntelligenceChat.scss";
import "./SpendAnalyticsAgent.scss";
import "./SpendAnalyticsConversations.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Bar as ChartBar,
  Line as ChartLine,
  Pie as ChartPie,
  Bubble as ChartBubble,
} from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  ChartDataLabels,
);

const normalizeSessionHistory = (payload) => {
  if (!Array.isArray(payload)) return [];
  return [...payload]
    .filter((item) => item && typeof item === "object" && item.session_id)
    .map((item) => ({
      sessionId: item.session_id,
      conversationId: item.conversation_id || "",
      title: item.title || "Untitled session",
      lastMessage: item.last_message || "",
      lastIntent: item.last_intent || "",
      messageCount: Number(item.message_count) || 0,
      turnCount: Number(item.turn_count) || 0,
      isPinned: Boolean(item.is_pinned),
      pinnedAt: item.pinned_at || null,
      createdAt: item.created_at || null,
      lastActiveAt: item.last_active_at || null,
      updatedAt: item.updated_at || null,
    }))
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
      const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
      return bTime - aTime;
    });
};

const mapSessionSummaryItem = (item) => {
  if (!item || typeof item !== "object" || !item.session_id) return null;
  return {
    sessionId: item.session_id,
    conversationId: item.conversation_id || "",
    title: item.title || "Untitled session",
    lastMessage: item.last_message || "",
    messageCount: Number(item.message_count) || 0,
    isPinned: Boolean(item.is_pinned),
    createdAt: item.created_at || null,
    lastActiveAt: item.last_active_at || null,
    pinnedAt: item.pinned_at || null,
    updatedAt: item.updated_at || null,
  };
};

const formatDataAsOn = (lastRefreshed) => {
  const dateObj = lastRefreshed ? new Date(lastRefreshed) : null;
  const valid = dateObj && !Number.isNaN(dateObj.getTime());
  const source = valid ? dateObj : new Date();
  return `Data As On: ${source.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

const formatThoughtDurationSeconds = (totalTimeMs) => {
  const ms = Number(totalTimeMs);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const seconds = ms / 1000;
  const rounded = Math.round(seconds * 10) / 10;
  return `${rounded}`.replace(/\.0$/, "");
};

const normalizeSessionMessages = (payload) => {
  if (!Array.isArray(payload)) return [];

  return payload
    .filter((item) => item && typeof item === "object")
    .map((item, idx) => {
      const role = typeof item.role === "string" ? item.role.toLowerCase() : "";
      const createdAt = item.created_at || item.createdAt || null;

      if (role === "user") {
        return {
          id: item.query_id || item.id || `user-${idx}`,
          queryId: item.query_id || item.id || null,
          type: "user",
          text: item.content || item.raw_query || "",
          createdAt,
        };
      }

      if (role === "assistant") {
        const responseData =
          item.response_data && typeof item.response_data === "object"
            ? item.response_data
            : {};

        const rawResponse =
          item.content || responseData.response || item.response || "";
        const { insight, responseWithoutInsights } =
          extractInsightsBlock(rawResponse);

        const sqlQueriesRaw = Array.isArray(item.sql_queries)
          ? item.sql_queries
          : Array.isArray(responseData.sql_queries)
            ? responseData.sql_queries
            : [];

        const sqlQueries = sqlQueriesRaw
          .filter((query) => typeof query === "string" && query.trim())
          .map((query) => query.trim());

        const followUps = Array.isArray(item.follow_up_questions)
          ? item.follow_up_questions
          : Array.isArray(responseData.follow_up_questions)
            ? responseData.follow_up_questions
            : [];

        const thoughtDurationSeconds = formatThoughtDurationSeconds(
          responseData.total_time_ms ?? item.total_time_ms,
        );

        return {
          id: item.query_id || item.id || `assistant-${idx}`,
          queryId: item.query_id || item.id || null,
          messageId: item.message_id || null,
          type: "ai",
          metadata: formatDataAsOn(
            item.last_refreshed || responseData.last_refreshed,
          ),
          thoughtDurationSeconds,
          insight,
          response: responseWithoutInsights,
          followUps,
          sourcesCount: sqlQueries.length,
          sqlQueries,
          sections: [],
          metrics: null,
          chartConfig: item.chart_config || responseData.chart_config || null,
          genieRawData:
            item.genie_raw_data || responseData.genie_raw_data || null,
          aiThinkingLines: buildAiThinkingLines(
            responseData.planner_reasoning?.reasoning_chain ||
              item.planner_reasoning?.reasoning_chain ||
              [],
            responseData.execution_plan?.steps ||
              responseData.planner_steps ||
              item.execution_plan?.steps ||
              item.planner_steps ||
              [],
          ),
          createdAt,
        };
      }

      return null;
    })
    .filter(Boolean);
};

const getRelativeTime = (isoTime) => {
  if (!isoTime) return "-";
  const targetMs = new Date(isoTime).getTime();
  if (Number.isNaN(targetMs)) return "-";
  const diffSec = Math.max(0, Math.floor((Date.now() - targetMs) / 1000));
  if (diffSec < 60) return "just now";
  const units = [
    [31536000, "year"],
    [2592000, "month"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [seconds, label] of units) {
    const count = Math.floor(diffSec / seconds);
    if (count >= 1) {
      return `${count} ${label}${count > 1 ? "s" : ""} ago`;
    }
  }
  return "just now";
};

const ThinkingAnimation = ({ queryIntentDone, plannerSteps }) => (
  <div className="sa-conv-thinking">
    <SpendAnalyticsBadge variant="message" className="sa-conv-ai-badge" />
    <div className="sa-conv-thinking-body">
      <div className="sa-conv-thinking-title">
        <span>
          <strong>Spend Analytics</strong> is currently processing your
          request. This may take a few moments as it analyzes the information
          and generates the best possible result. Please{" "}
          <strong>hold on</strong> while we complete this step.
        </span>
      </div>
      <div className="sa-conv-thinking-steps">
        {/* Fixed first step: Analyze query intent */}
        <div
          className={`sa-conv-thinking-step ${queryIntentDone ? "sa-conv-thinking-step--done" : "sa-conv-thinking-step--active"}`}
        >
          <span className="sa-conv-thinking-step-icon">
            {queryIntentDone ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10.0003 1.66663C5.40033 1.66663 1.66699 5.39996 1.66699 9.99996C1.66699 14.6 5.40033 18.3333 10.0003 18.3333C14.6003 18.3333 18.3337 14.6 18.3337 9.99996C18.3337 5.39996 14.6003 1.66663 10.0003 1.66663ZM10.0003 16.6666C6.32533 16.6666 3.33366 13.675 3.33366 9.99996C3.33366 6.32496 6.32533 3.33329 10.0003 3.33329C13.6753 3.33329 16.667 6.32496 16.667 9.99996C16.667 13.675 13.6753 16.6666 10.0003 16.6666ZM13.8253 6.31663L8.33366 11.8083L6.17533 9.65829L5.00033 10.8333L8.33366 14.1666L15.0003 7.49996L13.8253 6.31663Z"
                  fill="#129F14"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M13.5337 6.45833C12.5587 5.48333 11.2837 4.99166 10.0003 4.99166V9.99166L6.46699 13.525C8.41699 15.475 11.5837 15.475 13.542 13.525C15.492 11.575 15.492 8.40833 13.5337 6.45833ZM10.0003 1.65833C5.40033 1.65833 1.66699 5.39166 1.66699 9.99166C1.66699 14.5917 5.40033 18.325 10.0003 18.325C14.6003 18.325 18.3337 14.5917 18.3337 9.99166C18.3337 5.39166 14.6003 1.65833 10.0003 1.65833ZM10.0003 16.6583C6.31699 16.6583 3.33366 13.675 3.33366 9.99166C3.33366 6.30832 6.31699 3.32499 10.0003 3.32499C13.6837 3.32499 16.667 6.30832 16.667 9.99166C16.667 13.675 13.6837 16.6583 10.0003 16.6583Z"
                  fill="#60A3FC"
                />
              </svg>
            )}
          </span>
          <span>Analyze query intent</span>
        </div>
        {/* Dynamic execution steps from planner_complete event */}
        {plannerSteps.map((step, idx) => {
          const isDone =
            step.status === "complete" || step.source === "reasoning";
          const thoughtText =
            typeof step.thought === "string" ? step.thought.trim() : "";
          const descriptionText =
            typeof step.description === "string" ? step.description.trim() : "";
          const combinedStepText = thoughtText
            ? `${descriptionText}: ${thoughtText}`
            : descriptionText;
          return (
            <div
              key={idx}
              className={`sa-conv-thinking-step ${isDone ? "sa-conv-thinking-step--done" : "sa-conv-thinking-step--active"}`}
            >
              <span className="sa-conv-thinking-step-icon">
                {isDone ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10.0003 1.66663C5.40033 1.66663 1.66699 5.39996 1.66699 9.99996C1.66699 14.6 5.40033 18.3333 10.0003 18.3333C14.6003 18.3333 18.3337 14.6 18.3337 9.99996C18.3337 5.39996 14.6003 1.66663 10.0003 1.66663ZM10.0003 16.6666C6.32533 16.6666 3.33366 13.675 3.33366 9.99996C3.33366 6.32496 6.32533 3.33329 10.0003 3.33329C13.6753 3.33329 16.667 6.32496 16.667 9.99996C16.667 13.675 13.6753 16.6666 10.0003 16.6666ZM13.8253 6.31663L8.33366 11.8083L6.17533 9.65829L5.00033 10.8333L8.33366 14.1666L15.0003 7.49996L13.8253 6.31663Z"
                      fill="#129F14"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13.5337 6.45833C12.5587 5.48333 11.2837 4.99166 10.0003 4.99166V9.99166L6.46699 13.525C8.41699 15.475 11.5837 15.475 13.542 13.525C15.492 11.575 15.492 8.40833 13.5337 6.45833ZM10.0003 1.65833C5.40033 1.65833 1.66699 5.39166 1.66699 9.99166C1.66699 14.5917 5.40033 18.325 10.0003 18.325C14.6003 18.325 18.3337 14.5917 18.3337 9.99166C18.3337 5.39166 14.6003 1.65833 10.0003 1.65833ZM10.0003 16.6583C6.31699 16.6583 3.33366 13.675 3.33366 9.99166C3.33366 6.30832 6.31699 3.32499 10.0003 3.32499C13.6837 3.32499 16.667 6.30832 16.667 9.99166C16.667 13.675 13.6837 16.6583 10.0003 16.6583Z"
                      fill="#60A3FC"
                    />
                  </svg>
                )}
              </span>
              <span>{combinedStepText}</span>
            </div>
          );
        })}
        {/* Fixed last step: Generating insights - always pending */}
        <div className="sa-conv-thinking-step">
          <span className="sa-conv-thinking-step-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10.0003 1.66663C5.40033 1.66663 1.66699 5.39996 1.66699 9.99996C1.66699 14.6 5.40033 18.3333 10.0003 18.3333C14.6003 18.3333 18.3337 14.6 18.3337 9.99996C18.3337 5.39996 14.6003 1.66663 10.0003 1.66663ZM10.0003 16.6666C6.32533 16.6666 3.33366 13.675 3.33366 9.99996C3.33366 6.32496 6.32533 3.33329 10.0003 3.33329C13.6753 3.33329 16.667 6.32496 16.667 9.99996C16.667 13.675 13.6753 16.6666 10.0003 16.6666Z" fill="#B1B1B1"/>
            </svg>
          </span>
          <span>Generating insights</span>
        </div>
      </div>
      <div className="sa-conv-thinking-dots">
        <span className="sa-conv-thinking-dot sa-conv-thinking-dot--primary" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="6" fill="#3382E6" />
          </svg>
        </span>
        <span className="sa-conv-thinking-dot sa-conv-thinking-dot--secondary" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle opacity="0.5" cx="6" cy="6" r="6" fill="#60A3FC" />
          </svg>
        </span>
        <span className="sa-conv-thinking-dot sa-conv-thinking-dot--tertiary" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle opacity="0.3" cx="6" cy="6" r="6" fill="#60A3FC" />
          </svg>
        </span>
      </div>
    </div>
  </div>
);

// Parses a markdown table block into { headers, rows }
const parseMarkdownTable = (block) => {
  const lines = block
    .trim()
    .split("\n")
    .filter((l) => l.trim().startsWith("|") && l.trim().endsWith("|"))
    .filter((l) => !l.match(/^\s*\|[-:\s|]+\|\s*$/));
  if (lines.length === 0) return null;
  const parseRow = (line) =>
    line
      .split("|")
      .map((c) => c.trim())
      .filter((c) => c);
  return {
    headers: parseRow(lines[0]),
    rows: lines.slice(1).map(parseRow),
  };
};
const stringifyTableCell = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
};

const buildTableFromObjectRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const headers = Array.from(
    rows.reduce((acc, row) => {
      if (row && typeof row === "object" && !Array.isArray(row)) {
        Object.keys(row).forEach((key) => acc.add(key));
      }
      return acc;
    }, new Set()),
  );

  if (headers.length === 0) return null;

  return {
    headers,
    rows: rows.map((row) =>
      headers.map((header) => stringifyTableCell(row?.[header])),
    ),
  };
};

const normalizeGenieRawDataTable = (genieRawData) => {
  if (!genieRawData) return null;

  if (typeof genieRawData === "string") {
    const tableMatch = genieRawData.match(
      /(\|.+(?:\n\|[-:\s|]+\|)?(?:\n\|.+)*\|)/,
    );
    const parsed = parseMarkdownTable(tableMatch?.[0] || genieRawData);
    return parsed ? { ...parsed, title: "Details" } : null;
  }

  if (Array.isArray(genieRawData)) {
    if (genieRawData.length === 0) return null;

    if (
      genieRawData.every(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      )
    ) {
      const objectRowsTable = buildTableFromObjectRows(genieRawData);
      return objectRowsTable ? { ...objectRowsTable, title: "Details" } : null;
    }

    if (genieRawData.every((item) => Array.isArray(item))) {
      const rowArrays = genieRawData.map((row) => row.map(stringifyTableCell));
      if (rowArrays.length === 0) return null;
      const [headers, ...rows] = rowArrays;
      if (!headers || headers.length === 0) return null;
      return { headers, rows, title: "Details" };
    }

    return null;
  }

  if (typeof genieRawData === "object") {
    const title =
      typeof genieRawData.title === "string" && genieRawData.title.trim()
        ? genieRawData.title.trim()
        : "Details";

    if (
      Array.isArray(genieRawData.headers) &&
      Array.isArray(genieRawData.rows)
    ) {
      return {
        title,
        headers: genieRawData.headers.map(stringifyTableCell),
        rows: genieRawData.rows.map((row) =>
          Array.isArray(row)
            ? row.map(stringifyTableCell)
            : Array.isArray(genieRawData.headers)
              ? genieRawData.headers.map((header) =>
                  stringifyTableCell(row?.[header]),
                )
              : [],
        ),
      };
    }

    if (
      Array.isArray(genieRawData.columns) &&
      Array.isArray(genieRawData.data)
    ) {
      return {
        title,
        headers: genieRawData.columns.map(stringifyTableCell),
        rows: genieRawData.data.map((row) =>
          Array.isArray(row)
            ? row.map(stringifyTableCell)
            : genieRawData.columns.map((column) =>
                stringifyTableCell(row?.[column]),
              ),
        ),
      };
    }

    const fromNestedRows = buildTableFromObjectRows(genieRawData.rows);
    if (fromNestedRows) {
      return { ...fromNestedRows, title };
    }

    const fromNestedData = buildTableFromObjectRows(genieRawData.data);
    if (fromNestedData) {
      return { ...fromNestedData, title };
    }

    const fallbackEntries = Object.entries(genieRawData);
    if (fallbackEntries.length > 0) {
      return {
        title,
        headers: fallbackEntries.map(([key]) => key),
        rows: [fallbackEntries.map(([, value]) => stringifyTableCell(value))],
      };
    }
  }

  return null;
};

const SortIcon = ({ dir }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    style={{ verticalAlign: "middle", marginLeft: "4px", flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M12 5.83L15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83z"
      fill={dir === "asc" ? "#004895" : "#6B7280"}
    />
    <path
      d="M12 18.17L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z"
      fill={dir === "desc" ? "#004895" : "#6B7280"}
    />
  </svg>
);

const TABLE_VIEWPORT_HEIGHT = "296px";

const SortableHeaderLabel = ({ text }) => {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    checkTruncation();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span
      ref={textRef}
      style={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={isTruncated ? text : undefined}
    >
      {text}
    </span>
  );
};

const SortableTable = ({ headers, rows }) => {
  const [sortCol, setSortCol] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");

  const handleSort = (colIdx) => {
    if (sortCol === colIdx) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(colIdx);
      setSortDir("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    if (sortCol === null) return rows;
    return [...rows].sort((a, b) => {
      const av = (a[sortCol] || "").replace(/[^0-9.-]/g, "");
      const bv = (b[sortCol] || "").replace(/[^0-9.-]/g, "");
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      const cmp =
        !isNaN(an) && !isNaN(bn)
          ? an - bn
          : (a[sortCol] || "").localeCompare(b[sortCol] || "");
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortCol, sortDir]);

  const columnCount = Math.max(headers.length, 1);
  const useFixedLayout = columnCount <= 6;
  const minScrollableWidthPx = columnCount * 180;

  const thStyle = {
    borderTop: "2px solid #D8E7FB",
    borderBottom: "2px solid #D8E7FB",
    background: "#F5F9FF",
    padding: "10px 12px",
    fontSize: "12px",
    fontWeight: 700,
    color: "#2D2D2D",
    textAlign: "left",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: useFixedLayout ? `${100 / columnCount}%` : "auto",
  };
  const tdStyle = {
    borderBottom: "2px solid #D8E7FB",
    background: "#FFF",
    padding: "12px",
    fontSize: "13px",
    color: "#2D2D2D",
    whiteSpace: useFixedLayout ? "normal" : "nowrap",
    overflowWrap: "anywhere",
  };

  return (
    <table
      style={{
        borderCollapse: "collapse",
        width: "100%",
        minWidth: useFixedLayout ? "100%" : `${minScrollableWidthPx}px`,
        tableLayout: useFixedLayout ? "fixed" : "auto",
      }}
    >
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={thStyle} onClick={() => handleSort(i)}>
              <span
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <SortableHeaderLabel text={h} />
                <SortIcon dir={sortCol === i ? sortDir : null} />
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td key={ci} style={tdStyle}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── CHART HELPERS ──────────────────────────────────────────────────────────

const formatYTick = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return String(value);
};

const toNumber = (value) => {
  if (typeof value === "number") return value;
  const parsed = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Prefer null for missing values so Chart.js skipNull can drop empty grouped bars. */
const toNullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || ["none", "null", "nan", "n/a"].includes(normalized)) {
    return null;
  }
  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

const getText = (value) => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return String(value);
};

const truncateLabel = (label, maxLength = 12) => {
  if (!label || label.length <= maxLength) return label;
  return label.substring(0, maxLength - 1) + "…";
};

const getTitleText = (axisTitle) => {
  if (!axisTitle) return "";
  if (typeof axisTitle === "string") return axisTitle;
  return axisTitle.text || "";
};

const extractYearHints = (chartConfig) => {
  const cols = chartConfig?.columns_used?.primary_y;
  if (!Array.isArray(cols)) return [];
  return cols
    .map((col) => {
      const match = String(col).match(/(20\d{2})/);
      return match ? match[1] : null;
    })
    .filter(Boolean);
};

/** When the backend repeats series names (e.g. markets for 2024 then 2025), suffix years. */
const buildSeriesDisplayNames = (traces, yearHints = []) => {
  const nameCounts = {};
  traces.forEach((trace) => {
    const name = trace?.name || "";
    nameCounts[name] = (nameCounts[name] || 0) + 1;
  });
  const nameSeen = {};
  return traces.map((trace, index) => {
    const base = trace?.name || `series_${index + 1}`;
    if ((nameCounts[base] || 0) <= 1) return base;
    const occurrence = nameSeen[base] || 0;
    nameSeen[base] = occurrence + 1;
    const year = yearHints[occurrence] || String(occurrence + 1);
    return `${base} (${year})`;
  });
};

const normalizeChartType = (chartConfig, traces, layout) => {
  const rawType = (chartConfig?.chart_type || "").toLowerCase();
  if (rawType.includes("stack")) return "stacked_bar";
  if (rawType.includes("line")) return "line";
  if (rawType.includes("bubble")) return "bubble";
  if (rawType.includes("pie") || rawType.includes("donut")) return "pie";
  if (rawType.includes("bar") && layout?.barmode === "stack")
    return "stacked_bar";
  if (rawType.includes("bar")) return "bar";

  const traceType = (traces?.[0]?.type || "").toLowerCase();
  if (traceType === "pie") return "pie";
  if (traceType === "scatter") {
    const hasBubble = Array.isArray(traces?.[0]?.marker?.size);
    return hasBubble ? "bubble" : "line";
  }
  if (traceType === "bar" && layout?.barmode === "stack") return "stacked_bar";
  return "bar";
  // return traceType === "bar" ? "bar" : "bar";
};

const BAR_COLORS = [
  "#3380FF",
  "#8FBFFF",
  "#5AA3F5",
  "#0052CC",
  "#66B2FF",
  "#E87B35",
  "#F0A060",
  "#C45C1A",
  "#F5C48A",
  "#A84810",
];
const BAR_COLORS_2024 = ["#0052CC", "#3380FF", "#5AA3F5", "#66B2FF", "#8FBFFF"];
const BAR_COLORS_2025 = ["#C45C1A", "#E87B35", "#F0A060", "#F5C48A", "#A84810"];

const colorForSeries = (displayName, traceIndex) => {
  const yearMatch = String(displayName).match(/\((20\d{2})\)$/);
  if (yearMatch?.[1] === "2025") {
    return BAR_COLORS_2025[traceIndex % BAR_COLORS_2025.length];
  }
  if (yearMatch?.[1] === "2024") {
    return BAR_COLORS_2024[traceIndex % BAR_COLORS_2024.length];
  }
  return BAR_COLORS[traceIndex % BAR_COLORS.length];
};

/** Default off; set window.ENABLE_SA_MARKET_FACETS = true to enable (or in tests). */
const isSaMarketFacetsEnabled = () => {
  if (typeof window === "undefined") return false;
  const value = window.ENABLE_SA_MARKET_FACETS;
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  return false;
};

const shouldFacetByMarket = (chartType, traces, yearHints) => {
  if (!isSaMarketFacetsEnabled()) return false;
  if (chartType !== "bar" && chartType !== "stacked_bar") return false;
  if (!Array.isArray(yearHints) || yearHints.length < 2) return false;
  if (!Array.isArray(traces) || traces.length < 4) return false;

  const counts = {};
  traces.forEach((trace) => {
    const name = trace?.name || "";
    counts[name] = (counts[name] || 0) + 1;
  });
  const pairedNames = Object.keys(counts).filter((name) => counts[name] === 2);
  const pairedTraceCount = pairedNames.reduce(
    (sum, name) => sum + counts[name],
    0,
  );
  // Every series is an exact market×year pair (e.g. 5 markets × 2 years).
  return pairedNames.length >= 2 && pairedTraceCount === traces.length;
};

const buildMarketFacets = (traces, yearHints) => {
  const counts = {};
  traces.forEach((trace) => {
    const name = trace?.name || "";
    counts[name] = (counts[name] || 0) + 1;
  });
  const seen = {};
  const order = [];
  const byMarket = new Map();

  traces.forEach((trace, index) => {
    const market = trace?.name || `series_${index + 1}`;
    if ((counts[market] || 0) !== 2) return;
    const occurrence = seen[market] || 0;
    seen[market] = occurrence + 1;
    const year = yearHints[occurrence] || String(occurrence + 1);
    if (!byMarket.has(market)) {
      byMarket.set(market, { market, years: {} });
      order.push(market);
    }
    byMarket.get(market).years[year] = trace;
  });

  return order.map((market) => byMarket.get(market));
};

const buildFacetVendorLabels = (yearTraces) => {
  const labels = [];
  const seen = new Set();
  yearTraces.forEach((trace) => {
    (trace?.x || []).forEach((value) => {
      const label = getText(value);
      if (!label || seen.has(label)) return;
      seen.add(label);
      labels.push(label);
    });
  });
  return labels;
};

/**
 * Detect if any X-axis values appear more than once across all traces.
 * Returns true if duplicate X values are found, indicating stacking should be enabled.
 */
const detectDuplicateXValues = (traces) => {
  if (!Array.isArray(traces) || traces.length === 0) return false;
  
  const xCounts = {};
  traces.forEach((trace) => {
    (trace?.x || []).forEach((xVal) => {
      const label = getText(xVal);
      if (label) {
        xCounts[label] = (xCounts[label] || 0) + 1;
      }
    });
  });
  
  // Return true if any X value appears more than once
  return Object.values(xCounts).some((count) => count > 1);
};

/**
 * Transform a single trace with duplicate X values into multiple traces for stacking.
 * Each original data point becomes a separate trace positioned at its X value.
 */
const transformTraceForStacking = (traces) => {
  // Only transform if we have a single trace (common case from API)
  if (!Array.isArray(traces) || traces.length !== 1) return traces;
  
  const trace = traces[0];
  if (!Array.isArray(trace?.x) || !Array.isArray(trace?.y)) return traces;
  
  const xArray = (trace?.x || []).map(getText);
  const yArray = (trace?.y || []).map(toNullableNumber);
  const textArray = (trace?.text || []).map(getText);
  
  // Get unique X values in order of first appearance
  const uniqueXValues = Array.from(new Set(xArray));
  
  // Create one trace per data point, positioned at its X value
  const transformedTraces = yArray.map((yVal, idx) => {
    const currentXLabel = xArray[idx];
    
    // Align Y values: only the current point has a value, others are null
    const alignedYValues = uniqueXValues.map((xLabel) =>
      xLabel === currentXLabel ? yVal : null
    );
    
    const alignedTextValues = uniqueXValues.map((xLabel) =>
      xLabel === currentXLabel ? textArray[idx] : ""
    );
    
    return {
      ...trace,
      x: uniqueXValues,
      y: alignedYValues,
      text: alignedTextValues,
      name: trace.name || "Value", // All traces share the same name for legend
    };
  });
  
  return transformedTraces;
};

const maybeQualifyForecast = (seriesName, valueText) => {
  if (
    /forecast|fcst/i.test(seriesName || "") &&
    !/^FCST:/i.test(valueText || "")
  ) {
    return `FCST: ${valueText}`;
  }
  return valueText;
};

const getOrCreateTooltipElement = (chart) => {
  const parent = chart.canvas.parentNode;
  let tooltipEl = parent.querySelector(".sa-conv-chart-external-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "sa-conv-chart-external-tooltip";
    tooltipEl.style.opacity = 0;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const formatTooltipValue = (chartType, point) => {
  const seriesName = point?.dataset?.label || "Value";

  if (chartType === "bubble") {
    const raw = point?.raw || {};
    const valueText = raw.valueLabel || formatYTick(raw.y || 0);
    return {
      seriesName,
      valueText: maybeQualifyForecast(seriesName, valueText),
    };
  }

  const idx = point?.dataIndex ?? 0;
  const textLabels = point?.dataset?.textLabels || [];
  const parsedValue = chartType === "pie" ? point?.parsed : point?.parsed?.y;
  const fallback = formatYTick(parsedValue ?? 0);
  const valueText = textLabels[idx] || fallback;

  return {
    seriesName,
    valueText: maybeQualifyForecast(seriesName, valueText),
  };
};

const externalTooltipRenderer = (chartType, fullLabels = []) => (context) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltipElement(chart);

  if (!tooltip || tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  const firstPoint = tooltip.dataPoints?.[0];
  const dataIndex = firstPoint?.dataIndex;
  const datasetXLabels = Array.isArray(firstPoint?.dataset?.xLabels)
    ? firstPoint.dataset.xLabels
    : [];
  const datasetItemLabels = Array.isArray(firstPoint?.dataset?.itemLabels)
    ? firstPoint.dataset.itemLabels
    : [];
  const datasetSpecificLabel =
    dataIndex !== undefined ? datasetItemLabels[dataIndex] || datasetXLabels[dataIndex] : "";

  // Prefer the hovered L3 item label, then the L2 group label, before shared/global fallbacks.
  const title =
    datasetSpecificLabel ||
    tooltip.title?.[0] ||
    firstPoint?.label ||
    firstPoint?.raw?.xLabel ||
    (dataIndex !== undefined ? fullLabels[dataIndex] : "") ||
    "";

  const rowsHtml = (tooltip.dataPoints || [])
    .map((point) => {
      const { seriesName, valueText } = formatTooltipValue(chartType, point);
      return `<div class="sa-conv-chart-external-tooltip-row"><span class="sa-conv-chart-external-tooltip-name">${seriesName}:</span><span class="sa-conv-chart-external-tooltip-value"> ${valueText}</span></div>`;
    })
    .join("");

  tooltipEl.innerHTML = `
    ${title ? `<div class="sa-conv-chart-external-tooltip-label">${title}</div>` : ""}
    ${rowsHtml}
  `;

  const parentRect = chart.canvas.parentNode.getBoundingClientRect();
  let left = tooltip.caretX + 12;
  let top = tooltip.caretY + 12;

  tooltipEl.style.opacity = 1;
  const tipWidth = tooltipEl.offsetWidth;
  const tipHeight = tooltipEl.offsetHeight;
  const maxLeft = parentRect.width - tipWidth - 8;
  const maxTop = parentRect.height - tipHeight - 8;

  left = Math.max(8, Math.min(left, maxLeft));
  top = Math.max(8, Math.min(top, maxTop));

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
};

const SAConvChart = ({ chartConfig }) => {
  const wrapperRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(900);

  useEffect(() => {
    if (!wrapperRef.current || typeof ResizeObserver === "undefined")
      return undefined;
    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries?.[0]?.contentRect?.width;
      if (nextWidth) setChartWidth(nextWidth);
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const traces = chartConfig?.config?.data || [];
  const layout = chartConfig?.config?.layout || {};
  if (!traces.length) return null;

  const chartType = normalizeChartType(chartConfig, traces, layout);
  const hasStackableData = detectDuplicateXValues(traces);
  // Single-trace duplicate X → FE expands into N datasets for stacking (vendorId-style).
  const isSyntheticStack =
    hasStackableData && Array.isArray(traces) && traces.length === 1;

  // Transform single trace with duplicates into multiple traces for stacking
  const processedTraces = hasStackableData
    ? transformTraceForStacking(traces)
    : traces;
  
  const title = layout?.title?.text || "";
  const xAxisLabel = getTitleText(layout?.xaxis?.title);
  const yAxisLabel = getTitleText(layout?.yaxis?.title);
  const yAxis2Label = getTitleText(layout?.yaxis2?.title);
  const hasSecondaryYAxis =
    Boolean(layout?.yaxis2) ||
    processedTraces.some((trace) => (trace?.yaxis || "").toLowerCase() === "y2");

  const isCompact = chartWidth < 860;
  const isSmall = chartWidth < 640;
  const chartHeight = isSmall ? 280 : 340;
  const yearHints = extractYearHints(chartConfig);

  // Market×year payloads: one small 2024-vs-2025 chart per market (easy to revert via flag).
  if (shouldFacetByMarket(chartType, traces, yearHints)) {
    const facets = buildMarketFacets(traces, yearHints);
    const facetHeight = isSmall ? 200 : 230;
    const yearColor = {
      [yearHints[0]]: "#3380FF",
      [yearHints[1]]: "#E87B35",
    };

    return (
      <div
        className="sa-conv-chart-wrapper sa-conv-chart-wrapper--facets"
        ref={wrapperRef}
        data-testid="sa-market-facets"
      >
        {title && <div className="sa-conv-chart-title">{title}</div>}
        <div className="sa-conv-chart-facets">
          {facets.map((facet) => {
            const yearTraces = yearHints
              .map((year) => ({ year, trace: facet.years[year] }))
              .filter((entry) => entry.trace);
            const vendorLabels = buildFacetVendorLabels(
              yearTraces.map((entry) => entry.trace),
            );
            const tickLabels = vendorLabels.map((label) =>
              truncateLabel(label, 16),
            );
            const minCategoryWidth = 56;
            const requiredWidth = Math.max(
              chartWidth,
              vendorLabels.length * minCategoryWidth + 80,
            );
            const useScroll = requiredWidth > chartWidth + 16;
            const showFacetLabels =
              !isCompact && vendorLabels.length > 0 && vendorLabels.length <= 8;

            const datasets = yearTraces.map(({ year, trace }, yearIdx) => {
              const valueByVendor = new Map();
              const textByVendor = new Map();
              (trace?.x || []).forEach((xVal, idx) => {
                const key = getText(xVal);
                valueByVendor.set(key, toNullableNumber(trace?.y?.[idx]));
                textByVendor.set(key, getText(trace?.text?.[idx]));
              });
              const color =
                yearColor[year] ||
                (yearIdx === 0 ? "#3380FF" : "#E87B35");
              return {
                type: "bar",
                label: year,
                data: vendorLabels.map((label) =>
                  valueByVendor.has(label) ? valueByVendor.get(label) : null,
                ),
                textLabels: vendorLabels.map(
                  (label) => textByVendor.get(label) || "",
                ),
                xLabels: vendorLabels,
                itemLabels: vendorLabels,
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1,
                borderRadius: 3,
                maxBarThickness: 36,
                minBarLength: 8,
                categoryPercentage: 0.7,
                barPercentage: 0.9,
                skipNull: true,
              };
            });

            const numericValues = datasets.flatMap((ds) =>
              (ds.data || []).filter(
                (v) => v != null && Number.isFinite(Number(v)),
              ),
            );
            const suggestedMax =
              numericValues.length > 0
                ? Math.max(1, ...numericValues) * 1.15
                : undefined;

            const facetOptions = {
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: "nearest", intersect: false },
              layout: { padding: { top: 12, right: 8, bottom: 4, left: 4 } },
              scales: {
                x: {
                  ticks: {
                    autoSkip: true,
                    maxRotation: 35,
                    minRotation: 0,
                    color: "#595959",
                    font: { size: 10 },
                    callback: (_value, index) => tickLabels[index] || "",
                  },
                  grid: { display: false },
                  title: xAxisLabel
                    ? {
                        display: true,
                        text: xAxisLabel,
                        color: "#595959",
                        font: { size: 10 },
                      }
                    : { display: false },
                },
                y: {
                  suggestedMax,
                  ticks: {
                    callback: (value) => formatYTick(value),
                    color: "#595959",
                    font: { size: 10 },
                  },
                  grid: { color: "#F0F0F0", borderDash: [3, 3] },
                  title: yAxisLabel
                    ? {
                        display: true,
                        text: yAxisLabel,
                        color: "#595959",
                        font: { size: 10 },
                      }
                    : { display: false },
                },
              },
              plugins: {
                legend: {
                  display: true,
                  position: "top",
                  labels: {
                    boxWidth: 10,
                    color: "#2D2D2D",
                    font: { size: 11 },
                  },
                },
                tooltip: {
                  enabled: false,
                  external: externalTooltipRenderer("bar", vendorLabels),
                },
                datalabels: {
                  display: (ctx) => {
                    if (!showFacetLabels) return false;
                    const value = Number(ctx?.dataset?.data?.[ctx.dataIndex]);
                    return Number.isFinite(value) && value > 0;
                  },
                  anchor: "end",
                  align: "top",
                  color: "#595959",
                  font: { size: 9 },
                  formatter: (value, ctx) => {
                    const labelsArr = ctx.dataset?.textLabels || [];
                    return labelsArr[ctx.dataIndex] || formatYTick(value);
                  },
                },
              },
            };

            return (
              <div
                key={facet.market}
                className="sa-conv-chart-facet"
                data-testid="sa-chart-facet"
              >
                <div className="sa-conv-chart-facet-title">{facet.market}</div>
                <div
                  className={`sa-conv-chart-canvas${
                    useScroll ? " sa-conv-chart-canvas--scroll" : ""
                  }`}
                >
                  <div
                    style={{
                      height: `${facetHeight}px`,
                      width: useScroll ? `${requiredWidth}px` : "100%",
                      minWidth: useScroll ? `${requiredWidth}px` : undefined,
                    }}
                  >
                    <ChartBar
                      data={{ labels: tickLabels, datasets }}
                      options={facetOptions}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Transform Plotly-like traces into chart.js friendly arrays.
  // - Disjoint X sets (L2/L3 grouped bars): merge unique labels and align by name.
  // - Aligned series (incl. repeated X display names): keep every (x[i], y[i]) by index
  //   so duplicate vendor names each get their own category slot.
  const firstTrace = processedTraces[0];
  const xValues = firstTrace?.x || [];
  const sharedXLabels = xValues.map(getText);
  const hasDisjointXLabels =
    processedTraces.length > 1 &&
    processedTraces.some((trace, idx) => {
      const current = (trace?.x || []).map(getText);
      return processedTraces.some((other, otherIdx) => {
        if (idx === otherIdx) return false;
        const otherLabels = (other?.x || []).map(getText);
        return (
          current.length !== otherLabels.length ||
          current.some((label, labelIdx) => label !== otherLabels[labelIdx])
        );
      });
    });

  const combinedXLabels = Array.from(
    new Map(
      processedTraces.flatMap((trace) =>
        (trace?.x || []).map((value) => [getText(value), value]),
      ),
    ).keys(),
  );

  const displayLabels = hasDisjointXLabels ? combinedXLabels : sharedXLabels;
  const hasDuplicateDisplayLabels =
    !hasDisjointXLabels &&
    displayLabels.length !== new Set(displayLabels).size;
  // Keep `labels` as the human-facing category names (may contain duplicates).
  const labels = displayLabels;
  const axisTickLabels = labels.map((label) =>
    hasDisjointXLabels ? label : truncateLabel(label, 12),
  );
  // Chart.js category scale co-locates identical label strings; use unique ids when needed.
  const chartJsLabels = hasDuplicateDisplayLabels
    ? labels.map((_, idx) => `__sa_cat_${idx}`)
    : axisTickLabels;
  const truncatedLabels = chartJsLabels;
  const seriesDisplayNames = isSyntheticStack
    ? processedTraces.map(
        (trace, index) => trace?.name || `series_${index + 1}`,
      )
    : buildSeriesDisplayNames(processedTraces, yearHints);
  const syntheticStackColor = BAR_COLORS[0];

  const cartesianDatasets = processedTraces.map((trace, traceIndex) => {
    const traceType = (trace?.type || "bar").toLowerCase();
    const traceYaxis = (trace?.yaxis || "y").toLowerCase();
    const traceXLabels = (trace?.x || []).map(getText);
    const traceYValues = (trace?.y || []).map(toNullableNumber);
    const traceTextValues = (trace?.text || []).map(getText);
    const seriesLabel = seriesDisplayNames[traceIndex];
    const seriesColor = isSyntheticStack
      ? syntheticStackColor
      : colorForSeries(seriesLabel, traceIndex);

    let alignedValues;
    let alignedTextValues;
    let datasetItemLabels = labels;
    let datasetXLabels = labels;

    if (hasDisjointXLabels) {
      const valueMap = new Map();
      const textMap = new Map();
      traceXLabels.forEach((label, idx) => {
        // Last write wins within a single trace (rare); missing stay absent → null.
        valueMap.set(label, traceYValues[idx]);
        textMap.set(label, traceTextValues[idx] ?? "");
      });
      alignedValues = labels.map((label) =>
        valueMap.has(label) ? valueMap.get(label) : null,
      );
      alignedTextValues = labels.map((label) =>
        textMap.has(label) ? textMap.get(label) : "",
      );
    } else {
      // Index-aligned: one slot per (x,y) pair — do not collapse duplicate X names.
      alignedValues = labels.map((_, idx) =>
        idx < traceYValues.length ? traceYValues[idx] : null,
      );
      alignedTextValues = labels.map((_, idx) => traceTextValues[idx] ?? "");
      datasetItemLabels = labels;
      datasetXLabels = labels;
    }

    return {
      type: traceType === "line" || traceType === "scatter" ? "line" : "bar",
      yAxisID: traceYaxis === "y2" ? "y2" : "y",
      label: seriesLabel,
      xLabels: datasetXLabels,
      itemLabels: datasetItemLabels,
      data: alignedValues,
      textLabels: alignedTextValues,
      borderColor: isSyntheticStack ? "#FFFFFF" : seriesColor,
      backgroundColor: seriesColor,
      borderWidth: 2,
      borderSkipped: isSyntheticStack ? false : undefined,
      tension: 0.35,
      pointRadius: traceType === "line" || traceType === "scatter" ? 3 : 0,
      pointHoverRadius: traceType === "line" || traceType === "scatter" ? 5 : 0,
      fill: false,
      skipNull: true,
    };
  });

  const bubbleDatasets = processedTraces.map((trace, tIdx) => {
    const sizes = trace?.marker?.size || [];
    const points = (trace.x || []).map((xVal, idx) => {
      const yVal = toNumber(trace?.y?.[idx]);
      return {
        x: idx + 1,
        xLabel: getText(xVal),
        y: yVal,
        z: Math.max(6, toNumber(sizes?.[idx] ?? sizes ?? 14)),
        valueLabel: getText(trace?.text?.[idx]) || formatYTick(yVal),
      };
    });
    return {
      label: trace.name || `series_${tIdx + 1}`,
      data: points,
      borderColor: BAR_COLORS[tIdx % BAR_COLORS.length],
      backgroundColor: `${BAR_COLORS[tIdx % BAR_COLORS.length]}88`,
      pointRadius: (ctx) => ctx.raw?.z || 8,
      pointHoverRadius: (ctx) => (ctx.raw?.z || 8) + 2,
    };
  });
  const bubblePointCount = bubbleDatasets.reduce(
    (acc, s) => acc + s.data.length,
    0,
  );

  // Pie/donut data
  const pieTrace = processedTraces[0] || {};
  const pieLabels = (pieTrace.labels || []).map(getText);
  const truncatedPieLabels = pieLabels.map((label) => truncateLabel(label, 14));
  const pieValues = (pieTrace.values || []).map(toNumber);
  const pieText = (pieTrace.text || []).map(getText);

  const dataCount =
    chartType === "pie"
      ? pieLabels.length
      : chartType === "bubble"
        ? bubblePointCount
        : labels.length;

  const availableTickSlots = Math.max(
    2,
    Math.floor((chartWidth - 140) / (isSmall ? 70 : 90)),
  );
  const maxTicks = Math.max(2, availableTickSlots);
  const rotateXTicks = dataCount > 7 || isCompact;
  const seriesCount = processedTraces.length;
  const isDenseBarChart =
    (chartType === "bar" || chartType === "stacked_bar") &&
    (seriesCount > 4 ||
      labels.length > 12 ||
      seriesCount * Math.max(labels.length, 1) > 48);
  const showDataLabels =
    !isCompact &&
    !isDenseBarChart &&
    (chartType === "bar" || chartType === "stacked_bar");
  const minCategoryWidth = Math.max(40, 8 * Math.min(seriesCount, 10));
  const requiredChartWidth = Math.max(
    chartWidth,
    labels.length * minCategoryWidth + 96,
  );
  const useChartScroll =
    (chartType === "bar" || chartType === "stacked_bar") &&
    requiredChartWidth > chartWidth + 16;

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: false },
    layout: {
      padding: {
        top: 24,
        right: 10,
        bottom: 8,
        left: 10,
      },
    },
    scales: {
      x: {
        stacked: hasStackableData || chartType === "stacked_bar",
        ticks: {
          autoSkip: true,
          maxTicksLimit: maxTicks,
          maxRotation: rotateXTicks ? 35 : 0,
          minRotation: 0,
          align: "center",
          color: "#595959",
          font: { size: isSmall ? 10 : 12 },
          // Show human-facing names even when Chart.js categories are unique ids.
          callback: (_value, index) => axisTickLabels[index] || "",
          title: (context) => {
            return labels[context.index] || "";
          },
        },
        grid: { display: false },
        title: xAxisLabel
          ? {
              display: true,
              text: xAxisLabel,
              color: "#595959",
              font: { size: isSmall ? 10 : 12 },
            }
          : { display: false },
      },
      y: {
        stacked: hasStackableData || chartType === "stacked_bar",
        suggestedMax:
          chartType === "bar" && cartesianDatasets.length
            ? Math.max(
                1,
                ...cartesianDatasets.flatMap((ds) =>
                  (ds.data || []).filter(
                    (v) => v != null && Number.isFinite(Number(v)),
                  ),
                ),
              ) * 1.15
            : undefined,
        ticks: {
          callback: (value) => formatYTick(value),
          color: "#595959",
          font: { size: isSmall ? 10 : 12 },
        },
        grid: { color: "#F0F0F0", borderDash: [3, 3] },
        title: yAxisLabel
          ? {
              display: true,
              text: yAxisLabel,
              color: "#595959",
              font: { size: isSmall ? 10 : 12 },
            }
          : { display: false },
      },
      ...(hasSecondaryYAxis
        ? {
            y2: {
              position: "right",
              grid: { drawOnChartArea: false },
              ticks: {
                callback: (value) => formatYTick(value),
                color: "#595959",
                font: { size: isSmall ? 10 : 12 },
              },
              title: yAxis2Label
                ? {
                    display: true,
                    text: yAxis2Label,
                    color: "#595959",
                    font: { size: isSmall ? 10 : 12 },
                  }
                : { display: false },
            },
          }
        : {}),
    },
    plugins: {
      legend: {
        display: !isSyntheticStack,
        position: chartType === "pie" ? "bottom" : "top",
        labels: {
          boxWidth: 12,
          color: "#2D2D2D",
          font: { size: isSmall ? 10 : 12 },
        },
        onClick: (event, legendItem, legend) => {
          if (isSyntheticStack) return;
          const chart = legend.chart;
          const dsIndex = legendItem.datasetIndex;

          if (chartType !== "bar" && chartType !== "stacked_bar") {
            if (chart.isDatasetVisible(dsIndex)) {
              chart.hide(dsIndex);
              legendItem.hidden = true;
            } else {
              chart.show(dsIndex);
              legendItem.hidden = false;
            }
            return;
          }

          const meta = chart.getDatasetMeta(dsIndex);
          const wasVisible = meta.hidden !== true;
          meta.hidden = wasVisible ? true : null;
          legendItem.hidden = wasVisible;

          const visibleDatasetIndices = chart.data.datasets
            .map((_, idx) => idx)
            .filter((idx) => chart.getDatasetMeta(idx).hidden !== true);

          const keptIndices = labels
            .map((_, idx) => idx)
            .filter((idx) =>
              visibleDatasetIndices.some((vdsIdx) => {
                const raw = cartesianDatasets[vdsIdx]?.data?.[idx];
                const num = Number(raw);
                return raw != null && Number.isFinite(num) && num > 0;
              }),
            );

          chart.data.labels = keptIndices.map((idx) => truncatedLabels[idx]);
          chart.data.datasets.forEach((ds, dsIdx) => {
            const fullDs = cartesianDatasets[dsIdx];
            ds.data = keptIndices.map((idx) => fullDs.data[idx]);
            ds.textLabels = keptIndices.map((idx) => fullDs.textLabels[idx]);
            ds.xLabels = keptIndices.map((idx) => fullDs.xLabels[idx]);
            ds.itemLabels = keptIndices.map((idx) => fullDs.itemLabels[idx]);
          });

          chart.update();
        },
      },
      tooltip: {
        enabled: false,
        external: externalTooltipRenderer(chartType, labels),
        callbacks: {
          label: (context) => {
            const datasetItemLabels = context?.dataset?.itemLabels || [];
            const datasetLabels = context?.dataset?.xLabels || [];
            const fullLabel =
              datasetItemLabels[context.dataIndex] ||
              datasetLabels[context.dataIndex] ||
              labels[context.dataIndex] ||
              "";
            return fullLabel || context.label || "";
          },
        },
      },
      datalabels: {
        display: (ctx) => {
          if (chartType !== "bar" && chartType !== "stacked_bar") return false;
          const isVisible = ctx?.chart?.isDatasetVisible?.(ctx.datasetIndex);
          if (isVisible === false) return false;
          const meta = ctx?.chart?.getDatasetMeta?.(ctx.datasetIndex);
          if (meta?.hidden) return false;
          const value = Number(ctx?.dataset?.data?.[ctx.dataIndex] ?? 0);
          return showDataLabels && Number.isFinite(value) && value > 0;
        },
        anchor: "end",
        align: "top",
        color: "#595959",
        font: { size: 10, weight: "normal" },
        formatter: (value, ctx) => {
          const labelsArr = ctx.dataset?.textLabels || [];
          return labelsArr[ctx.dataIndex] || formatYTick(value);
        },
      },
    },
  };

  const barData = {
    labels: truncatedLabels,
    datasets: cartesianDatasets.map((dataset) => ({
      ...dataset,
      borderRadius:
        dataset.type === "line"
          ? 0
          : isSyntheticStack || chartType === "stacked_bar"
            ? 0
            : 3,
      // Keep low-value bars visible when one tall category dominates the Y scale.
      minBarLength: dataset.type === "line" ? undefined : 8,
      maxBarThickness: isDenseBarChart ? 28 : isSmall ? 36 : 60,
      categoryPercentage: isDenseBarChart ? 0.78 : 0.85,
      barPercentage: isDenseBarChart ? 0.88 : 0.92,
      skipNull: true,
    })),
  };

  const lineData = {
    labels: truncatedLabels,
    datasets: cartesianDatasets.map((dataset) => ({
      ...dataset,
      type: "line",
      fill: false,
    })),
  };

  const pieData = {
    labels: truncatedPieLabels,
    datasets: [
      {
        label: pieTrace.name || "Value",
        data: pieValues,
        textLabels: pieText,
        backgroundColor: pieLabels.map(
          (_, idx) => BAR_COLORS[idx % BAR_COLORS.length],
        ),
        borderColor: "#FFFFFF",
        borderWidth: 1,
      },
    ],
  };

  const bubbleData = {
    datasets: bubbleDatasets,
  };

  const bubbleOptions = {
    ...commonOptions,
    scales: {
      x: {
        ...commonOptions.scales.x,
        type: "linear",
        ticks: {
          ...commonOptions.scales.x.ticks,
          callback: (value) => {
            const idx = Math.max(0, Math.round(value) - 1);
            return truncatedLabels[idx] || "";
          },
        },
      },
      y: {
        ...commonOptions.scales.y,
      },
    },
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        enabled: false,
        external: externalTooltipRenderer(chartType, labels),
      },
      datalabels: {
        display: false,
      },
    },
  };

  const pieOptions = {
    ...commonOptions,
    scales: undefined,
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        enabled: false,
        external: externalTooltipRenderer(chartType, pieLabels),
      },
      datalabels: {
        display: showDataLabels && pieLabels.length <= 8,
        color: "#2D2D2D",
        font: { size: 10 },
        formatter: (_value, ctx) => truncatedPieLabels[ctx.dataIndex] || "",
      },
    },
  };

  return (
    <div className="sa-conv-chart-wrapper" ref={wrapperRef}>
      {title && <div className="sa-conv-chart-title">{title}</div>}
      <div
        className={`sa-conv-chart-canvas${
          useChartScroll ? " sa-conv-chart-canvas--scroll" : ""
        }`}
      >
        <div
          style={{
            height: `${chartHeight}px`,
            width: useChartScroll ? `${requiredChartWidth}px` : "100%",
            minWidth: useChartScroll ? `${requiredChartWidth}px` : undefined,
          }}
        >
          {chartType === "pie" && (
            <ChartPie data={pieData} options={pieOptions} />
          )}
          {chartType === "line" && (
            <ChartLine
              data={lineData}
              options={{
                ...commonOptions,
                plugins: {
                  ...commonOptions.plugins,
                  datalabels: { display: false },
                },
              }}
            />
          )}
          {chartType === "bubble" && (
            <ChartBubble data={bubbleData} options={bubbleOptions} />
          )}
          {(chartType === "bar" || chartType === "stacked_bar") && (
            <ChartBar data={barData} options={commonOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

const replaceMarkdownLinksLinear = (input) => {
  if (!input) return "";
  let out = "";
  let i = 0;

  while (i < input.length) {
    const openBracket = input.indexOf("[", i);
    if (openBracket === -1) {
      out += input.slice(i);
      break;
    }

    out += input.slice(i, openBracket);
    const closeBracket = input.indexOf("]", openBracket + 1);
    if (closeBracket === -1 || input[closeBracket + 1] !== "(") {
      out += input[openBracket];
      i = openBracket + 1;
      continue;
    }

    const closeParen = input.indexOf(")", closeBracket + 2);
    if (closeParen === -1) {
      out += input[openBracket];
      i = openBracket + 1;
      continue;
    }

    const text = input.slice(openBracket + 1, closeBracket);
    const url = input.slice(closeBracket + 2, closeParen);
    if (!text || !url) {
      out += input.slice(openBracket, closeParen + 1);
      i = closeParen + 1;
      continue;
    }

    out += `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    i = closeParen + 1;
  }

  return out;
};

const getLastLine = (text) => {
  if (!text) return "";
  const idx = text.lastIndexOf("\n");
  return idx >= 0 ? text.slice(idx + 1) : text;
};

const isAlphaChar = (ch) =>
  (ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z");

const normalizeInsightsHeading = (line) => {
  let value = (line || "").trim();
  if (value.startsWith("-")) value = value.slice(1).trim();
  if (value.startsWith("**")) value = value.slice(2);
  if (value.endsWith("**")) value = value.slice(0, -2);
  return value.trim();
};

const isInsightsHeadingLine = (line) => {
  const normalized = normalizeInsightsHeading(line).toLowerCase();
  return (
    normalized.startsWith("insight:") || normalized.startsWith("insights:")
  );
};

const isLikelySectionHeadingLine = (line) => {
  const t = (line || "").trim();
  if (!t) return false;
  if (t.startsWith("- ") || t.startsWith("* ")) return false;

  if (t.startsWith("**") && t.endsWith("**") && t.length > 4) {
    const inner = t.slice(2, -2).trim();
    const colonIdx = inner.indexOf(":");
    return colonIdx > 0 && isAlphaChar(inner[0]);
  }

  const colonIdx = t.indexOf(":");
  if (colonIdx <= 0) return false;
  return isAlphaChar(t[0]);
};

const extractInsightsBlock = (response) => {
  if (!response) return { insight: null, responseWithoutInsights: "" };

  const lines = response.split("\n");
  let startIdx = -1;
  let endIdx = -1;
  const insightLines = [];

  for (let i = 0; i < lines.length; i += 1) {
    if (!isInsightsHeadingLine(lines[i])) continue;

    startIdx = i;
    const headingLine = normalizeInsightsHeading(lines[i]);
    const colonIdx = headingLine.indexOf(":");
    const firstLine =
      colonIdx >= 0 ? headingLine.slice(colonIdx + 1).trim() : "";
    if (firstLine) insightLines.push(firstLine);

    let j = i + 1;
    while (j < lines.length && !isLikelySectionHeadingLine(lines[j])) {
      insightLines.push(lines[j]);
      j += 1;
    }
    endIdx = j;
    break;
  }

  if (startIdx === -1 || endIdx === -1) {
    return { insight: null, responseWithoutInsights: response };
  }

  const remainingLines = [...lines.slice(0, startIdx), ...lines.slice(endIdx)];
  const responseWithoutInsights = remainingLines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  let insightText = insightLines.join("\n").trim() || null;
  // Strip residual markdown bold markers (e.g. from "**Insights:**" extraction)
  if (insightText) {
    insightText = insightText.replace(/^\*\*\s{0,10}/, "").replace(/\s{0,10}\*\*$/, "");
  }

  return {
    insight: insightText,
    responseWithoutInsights,
  };
};

const capitalizeFirstWord = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return "";

  const parts = text.split(/\s+/);
  const first = parts[0] || "";
  parts[0] = `${first.charAt(0).toUpperCase()}${first.slice(1)}`;
  return parts.join(" ");
};

const normalizePlannerStepEntry = (step) => {
  const description =
    typeof step?.description === "string" ? step.description.trim() : "";
  const thought = typeof step?.thought === "string" ? step.thought.trim() : "";
  const status = typeof step?.status === "string" ? step.status : "active";
  const source = step?.source === "complete" ? "complete" : "reasoning";
  return { description, thought, status, source };
};

const mergePlannerCompleteWithThoughts = (existingSteps, executionSteps) => {
  const existing = Array.isArray(existingSteps)
    ? existingSteps.map(normalizePlannerStepEntry)
    : [];
  const plan = Array.isArray(executionSteps) ? executionSteps : [];

  const reasoningSteps = existing.filter((step) => step.source !== "complete");
  const completeSteps = existing.filter((step) => step.source === "complete");

  const updatedCompleteSteps = [...completeSteps];
  plan.forEach((step) => {
    const description =
      typeof step?.description === "string" ? step.description.trim() : "";
    if (!description) return;
    const status = typeof step?.status === "string" ? step.status : "active";
    const existingCompleteIndex = updatedCompleteSteps.findIndex(
      (item) =>
        typeof item.description === "string" &&
        item.description.trim().toLowerCase() === description.toLowerCase(),
    );

    if (existingCompleteIndex >= 0) {
      updatedCompleteSteps[existingCompleteIndex] = {
        ...updatedCompleteSteps[existingCompleteIndex],
        description,
        status,
        source: "complete",
      };
    } else {
      updatedCompleteSteps.push({
        description,
        thought: "",
        status,
        source: "complete",
      });
    }
  });

  return [...reasoningSteps, ...updatedCompleteSteps];
};

const applyPlannerReasoningStepUpdate = (existingSteps, reasoningStepRaw) => {
  const normalizedExisting = Array.isArray(existingSteps)
    ? existingSteps.map(normalizePlannerStepEntry)
    : [];
  const reasoningSteps = normalizedExisting.filter(
    (step) => step.source !== "complete",
  );
  const completeSteps = normalizedExisting.filter(
    (step) => step.source === "complete",
  );

  const next = [...reasoningSteps];
  const reasoningStep =
    reasoningStepRaw && typeof reasoningStepRaw === "object"
      ? reasoningStepRaw
      : {};

  const indexRaw = Number(reasoningStep.index);
  const index = Number.isInteger(indexRaw) && indexRaw >= 0 ? indexRaw : next.length;
  const totalRaw = Number(reasoningStep.total);
  const total = Number.isInteger(totalRaw) && totalRaw > 0 ? totalRaw : 0;

  if (total > next.length) {
    while (next.length < total) {
      next.push({
        description: "",
        thought: "",
        status: "active",
        source: "reasoning",
      });
    }
  }

  const current = next[index] || {
    description: "",
    thought: "",
    status: "active",
    source: "reasoning",
  };
  const descriptionRaw =
    typeof reasoningStep.step === "string"
      ? reasoningStep.step.trim()
      : typeof reasoningStep.description === "string"
        ? reasoningStep.description.trim()
        : "";
  const description = descriptionRaw
    ? capitalizeFirstWord(descriptionRaw)
    : current.description;
  const thought =
    typeof reasoningStep.thought === "string"
      ? reasoningStep.thought.trim()
      : current.thought;
  const status =
    typeof reasoningStep.status === "string" ? reasoningStep.status : "active";

  next[index] = { ...current, description, thought, status, source: "reasoning" };
  return [...next, ...completeSteps];
};

const buildAiThinkingLines = (reasoningChainInput, plannerStepsInput) => {
  const reasoningChain = Array.isArray(reasoningChainInput)
    ? reasoningChainInput
    : [];
  const reasoningLines = reasoningChain
    .map((entry) => {
      const stepRaw =
        typeof entry?.step === "string" ? entry.step.trim() : "";
      const thoughtRaw =
        typeof entry?.thought === "string" ? entry.thought.trim() : "";
      if (!stepRaw && !thoughtRaw) return null;
      const formattedStep = capitalizeFirstWord(stepRaw);
      return {
        step: formattedStep,
        thought: thoughtRaw,
      };
    })
    .filter(Boolean);

  if (reasoningLines.length > 0) {
    return reasoningLines;
  }

  const plannerSteps = Array.isArray(plannerStepsInput) ? plannerStepsInput : [];
  const stepDescriptions = plannerSteps
    .map((step) =>
      typeof step?.description === "string" ? step.description.trim() : "",
    )
    .filter(Boolean);
  const uniqueStepDescriptions = [...new Set(stepDescriptions)];

  return [
    { step: "Analyze", thought: "query intent" },
    ...uniqueStepDescriptions.map((description) => ({
      step: capitalizeFirstWord(description),
      thought: "",
    })),
    { step: "Generating", thought: "insights" },
  ];
};

const AIMessage = ({
  msg,
  onFollowUp,
  onShowSources,
  feedback,
  onFeedback,
  messageIndex,
  feedbackFormOpen,
  feedbackText,
  feedbackError,
  feedbackSubmitPending,
  feedbackAlreadySubmitted,
  onFeedbackTextChange,
  onFeedbackCancel,
  onFeedbackSubmit,
}) => {
  const [showAiThinking, setShowAiThinking] = useState(false);
  const isNegativeFeedback = feedback === "down";
  const feedbackFormIcon = isNegativeFeedback ? "thumb_down" : "thumb_up";
  const feedbackFormTitle = isNegativeFeedback
    ? "What went wrong?"
    : "What did you like?";
  const feedbackFormSubtitle = isNegativeFeedback
    ? "Let us know what happened and how we can improve:"
    : "Tell us what worked well for you";
  const feedbackPlaceholder = isNegativeFeedback
    ? "Describe the issue you experienced..."
    : "Share what stood out to you...";
  const sourceQueries = Array.isArray(msg.sqlQueries) ? msg.sqlQueries : [];
  const genieRawDataTable = React.useMemo(
    () => normalizeGenieRawDataTable(msg.genieRawData),
    [msg.genieRawData],
  );
  const sourceCount = sourceQueries.length || msg.sourcesCount || 0;
  const visibleBadgeCount = Math.min(sourceCount, 2);
  const disableFeedbackActions =
    feedbackSubmitPending || feedbackAlreadySubmitted;
  const aiThinkingLines = React.useMemo(() => {
    if (Array.isArray(msg.aiThinkingLines) && msg.aiThinkingLines.length > 0) {
      return msg.aiThinkingLines
        .map((line) => {
          if (typeof line === "string") {
            const [stepPart, ...thoughtParts] = line.split(":");
            return {
              step: capitalizeFirstWord(stepPart || ""),
              thought: thoughtParts.join(":").trim(),
            };
          }
          if (line && typeof line === "object") {
            return {
              step: capitalizeFirstWord(line.step || ""),
              thought:
                typeof line.thought === "string" ? line.thought.trim() : "",
            };
          }
          return null;
        })
        .filter((line) => line && (line.step || line.thought));
    }

    return [
      { step: "Analyze", thought: "query intent" },
      { step: "Generating", thought: "insights" },
    ];
  }, [msg.aiThinkingLines]);

  // Render markdown using ReactMarkdown (handles bold, italic, links natively)
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    );
  };

  // Split response into segments: text blocks and table blocks
  const renderResponse = (markdown) => {
    if (!markdown) return null;
    const tableRegex = /(\|.+(?:\n\|[-:\s|]+\|)?(?:\n\|.+)*\|)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = tableRegex.exec(markdown)) !== null) {
      // Text before the table
      const before = markdown.slice(lastIndex, match.index).trim();
      if (before) {
        parts.push(
          <div key={key++} className="sa-conv-response-text">
            {renderMarkdown(before)}
          </div>,
        );
      }
      // Parse and render the table
      const parsed = parseMarkdownTable(match[0]);
      if (parsed) {
        const title = getLastLine(markdown.slice(0, match.index))
          .replace(/\*\*/g, "")
          .trim();
        parts.push(
          <div
            key={key++}
            style={{
              marginLeft: "48px",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            <CardV2
              state="non-interactive"
              type="basic"
              nested={true}
              cardHeader={{ title: title || "Details", type: "default" }}
              cardBody={{
                type: "slot",
                slot: (
                  <div
                    style={{
                      width: "100%",
                      maxHeight: TABLE_VIEWPORT_HEIGHT,
                      overflowX: "auto",
                      overflowY: "auto",
                    }}
                    className="sa-conv-card-table-slot"
                  >
                    <SortableTable
                      headers={parsed.headers}
                      rows={parsed.rows}
                    />
                  </div>
                ),
              }}
              viewport="desktop"
              style={{ borderColor: "#D8E7FB" }}
            />
          </div>,
        );
      }
      lastIndex = match.index + match[0].length;
    }

    // Remaining text after last table
    const after = markdown.slice(lastIndex).trim();
    if (after) {
      parts.push(
        <div key={key++} className="sa-conv-response-text">
          {renderMarkdown(after)}
        </div>,
      );
    }

    return parts.length > 0 ? (
      parts
    ) : (
      <div className="sa-conv-response-text">{renderMarkdown(markdown)}</div>
    );
  };

  const renderGenieRawDataTable = (tableData) => {
    if (!tableData || !Array.isArray(tableData.headers) || !Array.isArray(tableData.rows)) {
      return null;
    }

    return (
      <div
        style={{
          marginLeft: "48px",
          marginTop: "12px",
          marginBottom: "12px",
        }}
      >
        <CardV2
          state="non-interactive"
          type="basic"
          nested={true}
          cardHeader={{ title: tableData.title || "Details", type: "default" }}
          cardBody={{
            type: "slot",
            slot: (
              <div
                style={{
                  width: "100%",
                  maxHeight: TABLE_VIEWPORT_HEIGHT,
                  overflowX: "auto",
                  overflowY: "auto",
                }}
                className="sa-conv-card-table-slot"
              >
                <SortableTable
                  headers={tableData.headers}
                  rows={tableData.rows}
                />
              </div>
            ),
          }}
          viewport="desktop"
          style={{ borderColor: "#D8E7FB" }}
        />
      </div>
    );
  };

  return (
    <div className="sa-conv-ai-msg">
      <div className="sa-conv-ai-header">
        <SpendAnalyticsBadge variant="message" className="sa-conv-ai-badge" />
        <div className="sa-conv-ai-meta">
          <span className="sa-conv-ai-label">
            GENERATED BY SPEND ANALYTICS
          </span>
          <span className="sa-conv-ai-metadata">{msg.metadata}</span>
          {msg.thoughtDurationSeconds !== null &&
            msg.thoughtDurationSeconds !== undefined && (
              <span className="sa-conv-ai-metadata sa-conv-ai-thought-time">
                {`Thought for ${msg.thoughtDurationSeconds}s`}
              </span>
            )}
        </div>
      </div>

      <div
        className={`sa-conv-ai-thinking${showAiThinking ? " sa-conv-ai-thinking--expanded" : ""}`}
        role="group"
        aria-label="AI Thinking"
      >
        <span className="sa-conv-ai-thinking-rail" aria-hidden="true" />
        <div className="sa-conv-ai-thinking-content">
          <button
            type="button"
            className="sa-conv-ai-thinking-toggle"
            onClick={() => setShowAiThinking((prev) => !prev)}
            aria-expanded={showAiThinking}
            aria-label={showAiThinking ? "Hide AI Thinking" : "Show AI Thinking"}
          >
            <span className="sa-conv-ai-thinking-label">Show AI Thinking</span>
            {showAiThinking ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path d="M19 11V13H5V11H19Z" fill="#2E9688" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path d="M13 5V11H19V13H13V19H11V13H5L5 11H11V5H13Z" fill="#2E9688" />
              </svg>
            )}
          </button>

          {showAiThinking && (
            <div className="sa-conv-ai-thinking-lines">
              {aiThinkingLines.map((line, idx) => (
                <div
                  key={`${line.step}-${line.thought}-${idx}`}
                  className="sa-conv-ai-thinking-line"
                >
                  <span className="sa-conv-ai-thinking-bullet">-</span>
                  <strong>{line.step}</strong>
                  {line.thought ? `: ${line.thought}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {msg.metrics && (
        <div className="sa-conv-metrics">
          <div className="sa-conv-metrics-label">{msg.metrics.label}</div>
          <div className="sa-conv-metrics-values">
            {msg.metrics.values.map((v, i) => (
              <span key={i} className="sa-conv-metrics-value">
                <strong>{v.label}</strong> {v.value}
                {i < msg.metrics.values.length - 1 && (
                  <span className="sa-conv-metrics-sep"> | </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {msg.insight && (
        <div className="sa-conv-insight">
          <div className="sa-conv-insight-label">
            <span className="sa-conv-insight-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                  fill="#027C06"
                />
              </svg>
            </span>
            <span>Insight</span>
          </div>
          <p>{renderMarkdown(msg.insight)}</p>
        </div>
      )}

      {msg.response && renderResponse(msg.response)}
      {msg.chartConfig && <SAConvChart chartConfig={msg.chartConfig} />}
      {genieRawDataTable && renderGenieRawDataTable(genieRawDataTable)}

      {msg.sections &&
        msg.sections.map((section, i) => (
          <div key={i} className="sa-conv-section">
            <div className="sa-conv-section-title">{section.title}</div>
            <ul className="sa-conv-section-bullets">
              {section.bullets.map((b, j) => (
                <li key={j}>{b}</li>
              ))}
            </ul>
          </div>
        ))}

      <div className="sa-conv-feedback-section">
        <div className="sa-conv-actions">
          <ButtonIcon
            type="naked"
            size="small"
            active={feedback === "up"}
            icon="thumb_up"
            label="Thumbs up"
            disabled={disableFeedbackActions}
            onClick={() => onFeedback("up")}
          />
          <ButtonIcon
            type="naked"
            size="small"
            active={feedback === "down"}
            icon="thumb_down"
            label="Thumbs down"
            disabled={disableFeedbackActions}
            onClick={() => onFeedback("down")}
          />
          <div className="sa-conv-actions-divider" />
          <button
            className="sa-conv-sources-chip"
            onClick={() => onShowSources(sourceQueries)}
            title="View sources"
          >
            <div className="sa-conv-sources-badges">
              {Array.from({ length: visibleBadgeCount }, (_, i) => i).map(
                (i) => (
                  <div
                    key={i}
                    className="sa-conv-source-badge"
                    style={{
                      zIndex: visibleBadgeCount - i,
                    }}
                  >
                    <div className="sa-conv-source-badge-initial">
                      {String.fromCharCode(65 + i)}
                    </div>
                  </div>
                ),
              )}
              {sourceCount > 2 && (
                <div className="sa-conv-source-badge-count">
                  +{sourceCount - 2}
                </div>
              )}
            </div>
            <span className="sa-conv-sources-label">Sources</span>
          </button>
        </div>

        {feedbackFormOpen === messageIndex && (
          <CardV2
            state="interactive"
            type="basic"
            nested={true}
            className="sa-conv-feedback-card"
            cardBody={{
              type: "slot",
              slot: (
                <div className="sa-conv-feedback-content">
                  <div className="sa-conv-feedback-header">
                    <Icon
                      icon={feedbackFormIcon}
                      className="sa-conv-feedback-icon"
                    />
                    <h3 className="sa-conv-feedback-title">
                      {feedbackFormTitle}
                    </h3>
                  </div>

                  <p className="sa-conv-feedback-subtitle">
                    {feedbackFormSubtitle}
                  </p>

                  <textarea
                    className="sa-conv-feedback-textarea"
                    placeholder={feedbackPlaceholder}
                    value={feedbackText}
                    onChange={(e) => onFeedbackTextChange(e.target.value)}
                  />

                  {feedbackError && (
                    <p className="sa-conv-feedback-error">{feedbackError}</p>
                  )}

                  <div className="sa-conv-feedback-info">
                    <Feedback message="Your conversation will be included with your feedback to help improve the AI agent." />
                  </div>

                  <div className="sa-conv-feedback-actions">
                    <Button
                      text="Cancel"
                      size="small"
                      disabled={feedbackSubmitPending}
                      onClick={onFeedbackCancel}
                    />
                    <Button
                      text={feedbackSubmitPending ? "Submitting..." : "Submit"}
                      size="small"
                      disabled={
                        feedbackSubmitPending || feedbackAlreadySubmitted
                      }
                      onClick={() =>
                        onFeedbackSubmit(messageIndex, msg, feedback)
                      }
                    />
                  </div>
                </div>
              ),
            }}
          />
        )}
      </div>

      {msg.followUps && msg.followUps.length > 0 && (
        <div className="sa-conv-followups">
          <div className="sa-conv-followups-title">Follow-ups</div>
          {msg.followUps.map((f, i) => (
            <button
              key={i}
              className="sa-conv-followup-btn"
              onClick={() => onFollowUp(f)}
            >
              <svg
                className="sa-conv-followup-arrow"
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="12"
                viewBox="0 0 19 12"
                fill="none"
              >
                <path
                  d="M17 1V5H3.83L7.41 1.41L6 0L0 6L6 12L7.41 10.59L3.83 7H19V1H17Z"
                  fill="#2D2D2D"
                />
              </svg>
              <span>{f}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SourceQueryItem = ({ sourceText, index, isExpanded, onToggle }) => {
  const descRef = useRef(null);
  const [isExpandable, setIsExpandable] = useState(false);

  useEffect(() => {
    const el = descRef.current;
    if (!el || isExpanded) {
      setIsExpandable(false);
      return;
    }
    setIsExpandable(el.scrollHeight > el.clientHeight + 1);
  }, [sourceText, isExpanded]);

  return (
    <div className="sa-conv-source-item">
      <div className="sa-conv-source-title">Source {index + 1}</div>
      <div
        ref={descRef}
        className={`sa-conv-source-desc${isExpanded ? " sa-conv-source-desc--expanded" : ""}`}
      >
        {sourceText}
      </div>
      {(isExpandable || isExpanded) && (
        <button className="sa-conv-source-expand-btn" onClick={onToggle}>
          {isExpanded ? "Show Less" : "Show More"}
        </button>
      )}
    </div>
  );
};

let toastIdCounter = 0;

const deriveGlobalLoadingState = (statusByChatId) => {
  const entries = Object.entries(statusByChatId || {});
  const loadingEntries = entries.filter(([, status]) => status === "loading");

  if (loadingEntries.length > 0) {
    return {
      loadingStatus: "loading",
      loadingChatId: loadingEntries[loadingEntries.length - 1][0],
    };
  }

  if (entries.some(([, status]) => status === "error")) {
    return { loadingStatus: "error", loadingChatId: null };
  }

  if (entries.some(([, status]) => status === "done")) {
    return { loadingStatus: "done", loadingChatId: null };
  }

  return { loadingStatus: "idle", loadingChatId: null };
};

const SpendAnalyticsConversations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const activeChatIdRef = useRef(null);
  const sseAbortRef = useRef(new Map());
  const activePollByChatId = useRef(new Map());

  // Get context for sharing session history
  const sessionContext = useContext(SpendAnalyticsSessionContext);

  // Check if navigation state exists (true on navigation, false/null on page load)
  const hasNavigationState =
    location.state !== null && location.state !== undefined;
  const initialMessage = location.state?.message || "";
  const initialSessionId = location.state?.session_id || null;
  const initialThreadId =
    location.state?.conversation_id || location.state?.thread_id || null;

  const [messages, setMessages] = useState([]);
  const [messagesByChatId, setMessagesByChatId] = useState({});
  const [pollUpdatesByChatId, setPollUpdatesByChatId] = useState({});
  const [activeSessionIdByChatId, setActiveSessionIdByChatId] = useState({});
  const [threadIdByChatId, setThreadIdByChatId] = useState({});
  const [isThinkingByChatId, setIsThinkingByChatId] = useState({});
  const [queryIntentDoneByChatId, setQueryIntentDoneByChatId] = useState({});
  const [plannerStepsByChatId, setPlannerStepsByChatId] = useState({});
  const [loadingStatusByChatId, setLoadingStatusByChatId] = useState({});
  const [sessionMessagesLoadingByChatId, setSessionMessagesLoadingByChatId] =
    useState({});
  const [sessionMessagesErrorByChatId, setSessionMessagesErrorByChatId] =
    useState({});
  const [sessionMessagesRetryableByChatId, setSessionMessagesRetryableByChatId] =
    useState({});
  const [message, setMessage] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [showSources, setShowSources] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState("idle");
  const [loadingChatId, setLoadingChatId] = useState(null);
  const [activeChatId, setActiveChatId] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [queryIntentDone, setQueryIntentDone] = useState(false);
  const [plannerSteps, setPlannerSteps] = useState([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [lastProcessedMessage, setLastProcessedMessage] = useState(null);
  const [activeSqlQueries, setActiveSqlQueries] = useState([]);
  const [expandedSourceItems, setExpandedSourceItems] = useState({});
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [activeHistorySessionId, setActiveHistorySessionId] = useState(null);
  const [isSessionMessagesLoading, setIsSessionMessagesLoading] =
    useState(false);
  const [sessionMessagesError, setSessionMessagesError] = useState("");
  const [sessionMessagesRetryable, setSessionMessagesRetryable] =
    useState(false);
  const [messageFeedback, setMessageFeedback] = useState({});
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSubmitPendingByMessage, setFeedbackSubmitPendingByMessage] =
    useState({});
  const [submittedFeedbackByMessage, setSubmittedFeedbackByMessage] = useState(
    {},
  );
  const [feedbackToasts, setFeedbackToasts] = useState([]);
  const [recentMenuOpenId, setRecentMenuOpenId] = useState(null);
  const [pinLoadingById, setPinLoadingById] = useState({});
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const sessionMessagesRequestIdRef = useRef({});

  const isRequestInFlight = loadingStatus === "loading";
  const isActiveChatLoading =
    !!activeChatId && loadingStatusByChatId[activeChatId] === "loading";

  const syncActiveChatState = React.useCallback(
    (chatId) => {
      if (!chatId) {
        setMessages([]);
        setIsThinking(false);
        setQueryIntentDone(false);
        setPlannerSteps([]);
        setSessionId(null);
        setThreadId(null);
        setIsSessionMessagesLoading(false);
        setSessionMessagesError("");
        setSessionMessagesRetryable(false);
        return;
      }

      setMessages(messagesByChatId[chatId] || []);
      setIsThinking(Boolean(isThinkingByChatId[chatId]));
      setQueryIntentDone(Boolean(queryIntentDoneByChatId[chatId]));
      setPlannerSteps(plannerStepsByChatId[chatId] || []);
      setSessionId(activeSessionIdByChatId[chatId] || chatId || null);
      setThreadId(threadIdByChatId[chatId] || null);
      setIsSessionMessagesLoading(Boolean(sessionMessagesLoadingByChatId[chatId]));
      setSessionMessagesError(sessionMessagesErrorByChatId[chatId] || "");
      setSessionMessagesRetryable(
        Boolean(sessionMessagesRetryableByChatId[chatId]),
      );
    },
    [
      messagesByChatId,
      isThinkingByChatId,
      queryIntentDoneByChatId,
      plannerStepsByChatId,
      activeSessionIdByChatId,
      threadIdByChatId,
      sessionMessagesLoadingByChatId,
      sessionMessagesErrorByChatId,
      sessionMessagesRetryableByChatId,
    ],
  );

  const setChatMessages = React.useCallback((chatId, updater) => {
    if (!chatId) return;
    setMessagesByChatId((prev) => {
      const current = prev[chatId] || [];
      const nextMessages =
        typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [chatId]: nextMessages };
    });
  }, []);

  const setChatLoadingStatus = React.useCallback((chatId, status) => {
    if (!chatId) return;
    setLoadingStatusByChatId((prev) => ({
      ...prev,
      [chatId]: status,
    }));
  }, []);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    const nextGlobalState = deriveGlobalLoadingState(loadingStatusByChatId);
    setLoadingStatus(nextGlobalState.loadingStatus);
    setLoadingChatId(nextGlobalState.loadingChatId);
  }, [loadingStatusByChatId]);

  useEffect(() => {
    syncActiveChatState(activeChatId);
  }, [activeChatId, syncActiveChatState]);

  const getFeedbackMessageKey = (messageIndex, messageData) => {
    const scopedSessionId =
      activeSessionIdByChatId[activeChatId] ||
      sessionId ||
      activeHistorySessionId ||
      "unknown";
    const scopedMessageId =
      messageData?.queryId || messageData?.id || `idx-${messageIndex}`;
    return `${scopedSessionId}:${scopedMessageId}`;
  };

  const pushFeedbackToast = (type, text, duration = 6000) => {
    setFeedbackToasts((prev) => [
      ...prev,
      {
        id: ++toastIdCounter,
        duration,
        text,
        type,
      },
    ]);
  };

  const removeFeedbackToast = (toastId) => {
    setFeedbackToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  };

  const handleMessageFeedback = (messageIndex, feedbackType, messageData) => {
    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);

    if (feedbackSubmitPendingByMessage[feedbackKey]) {
      return;
    }

    if (submittedFeedbackByMessage[feedbackKey]) {
      pushFeedbackToast(
        "info",
        "Feedback already submitted for this response.",
      );
      return;
    }

    const currentFeedback = messageFeedback[messageIndex] || null;
    const nextFeedback = currentFeedback === feedbackType ? null : feedbackType;

    setMessageFeedback((prev) => ({
      ...prev,
      [messageIndex]: nextFeedback,
    }));

    setFeedbackFormOpen(nextFeedback ? messageIndex : null);
    setFeedbackError("");
    setFeedbackText("");
  };

  const handleCloseFeedbackForm = () => {
    setFeedbackFormOpen(null);
    setFeedbackError("");
    setFeedbackText("");
  };

  const closeRecentMenu = () => {
    setRecentMenuOpenId(null);
  };

  const handleTogglePinSession = async (sessionEntry) => {
    if (!sessionEntry?.sessionId) return;
    const targetSessionId = sessionEntry.sessionId;
    const shouldPin = !Boolean(sessionEntry.isPinned);

    setPinLoadingById((prev) => ({ ...prev, [targetSessionId]: true }));
    setHistoryError("");

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/${targetSessionId}/pin`,
        {
          method: "PATCH",
          headers: {
            Authorization,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_pinned: shouldPin }),
        },
      );
      if (!res.ok) {
        throw new Error(`Pin session failed: ${res.status}`);
      }

      let payload = null;
      try {
        payload = await res.json();
      } catch (_err) {
        payload = null;
      }

      const normalizedItem = mapSessionSummaryItem(payload);
      setSessionHistory((prev) => {
        const next = prev.map((entry) => {
          if (entry.sessionId !== targetSessionId) return entry;
          if (normalizedItem) return { ...entry, ...normalizedItem };
          const updatedAt = new Date().toISOString();
          return {
            ...entry,
            isPinned: shouldPin,
            pinnedAt: shouldPin ? updatedAt : null,
            updatedAt,
          };
        });
        return next.sort((a, b) => {
          if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
          const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
          const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
          return bTime - aTime;
        });
      });
    } catch (err) {
      console.error("Unable to update pin status:", err);
      setHistoryError("Unable to update pin status.");
    } finally {
      setPinLoadingById((prev) => ({ ...prev, [targetSessionId]: false }));
      closeRecentMenu();
    }
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteTargetSessionId) return;
    setIsDeletingSession(true);
    setHistoryError("");

    const deletedSessionId = deleteTargetSessionId;

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/${deletedSessionId}`,
        {
          method: "DELETE",
          headers: { Authorization },
        },
      );
      if (!res.ok) {
        throw new Error(`Delete session failed: ${res.status}`);
      }

      setSessionHistory((prev) => {
        const nextHistory = prev.filter(
          (entry) => entry.sessionId !== deletedSessionId,
        );

        if (activeHistorySessionId !== deletedSessionId) {
          return nextHistory;
        }

        const deletedIndex = prev.findIndex(
          (entry) => entry.sessionId === deletedSessionId,
        );
        const fallbackSession =
          nextHistory[deletedIndex] || nextHistory[deletedIndex - 1] || null;

        if (fallbackSession) {
          loadSessionMessages(
            fallbackSession.sessionId,
            fallbackSession.conversationId,
          );
        } else {
          setActiveChatId(null);
          setActiveHistorySessionId(null);
          setSessionId(null);
          setThreadId(null);
          setMessages([]);
          setShowSources(false);
          setActiveSqlQueries([]);
          setExpandedSourceItems({});
        }

        return nextHistory;
      });

      setDeleteTargetSessionId(null);
      closeRecentMenu();
    } catch (err) {
      console.error("Unable to delete session:", err);
      setHistoryError("Unable to delete session.");
    } finally {
      setIsDeletingSession(false);
    }
  };

  const handleSubmitFeedback = async (
    messageIndex,
    messageData,
    feedbackType,
  ) => {
    const selectedFeedback =
      feedbackType || messageFeedback[messageIndex] || null;
    if (!selectedFeedback) {
      setFeedbackError("Select thumbs up or thumbs down to continue.");
      return;
    }

    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) {
      return;
    }

    if (submittedFeedbackByMessage[feedbackKey]) {
      setFeedbackError("");
      pushFeedbackToast(
        "info",
        "Feedback already submitted for this response.",
      );
      return;
    }

    const scopedSessionId =
      activeSessionIdByChatId[activeChatId] || sessionId || activeHistorySessionId;
    if (!scopedSessionId) {
      setFeedbackError("Unable to submit feedback right now. Missing session.");
      pushFeedbackToast(
        "error",
        "Unable to submit feedback right now. Please retry.",
      );
      return;
    }

    const queryId = messageData?.queryId || messageData?.id || null;
    if (!queryId || typeof queryId !== "string") {
      setFeedbackError("Unable to submit feedback for this response.");
      pushFeedbackToast(
        "error",
        "Unable to submit feedback for this response. Please retry.",
      );
      return;
    }

    const trimmedComment = feedbackText.trim();
    if (trimmedComment.length > 1000) {
      setFeedbackError("Comment must be 1000 characters or fewer.");
      return;
    }

    const payload = {
      query_id: queryId,
      rating: selectedFeedback === "up" ? "thumbs_up" : "thumbs_down",
    };

    if (messageData?.messageId && typeof messageData.messageId === "string") {
      payload.message_id = messageData.messageId;
    }

    if (trimmedComment) {
      payload.comment = trimmedComment;
    }

    const params = new URLSearchParams({ session_id: scopedSessionId });
    if (threadId) {
      params.set("conversation_id", threadId);
    }

    setFeedbackSubmitPendingByMessage((prev) => ({
      ...prev,
      [feedbackKey]: true,
    }));
    setFeedbackError("");

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/feedback?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      let responseData = null;
      try {
        responseData = await res.json();
      } catch (parseErr) {
        responseData = null;
      }

      if (!res.ok) {
        if (res.status === 400) {
          const validationMessage =
            responseData?.message ||
            "Please check your feedback input and try again.";
          setFeedbackError(validationMessage);
          pushFeedbackToast("warning", validationMessage);
          return;
        }

        const errorMessage =
          responseData?.message ||
          "Unable to save feedback right now. Please try again.";
        setFeedbackError(errorMessage);
        pushFeedbackToast("error", errorMessage);
        return;
      }

      setSubmittedFeedbackByMessage((prev) => ({
        ...prev,
        [feedbackKey]: {
          feedback_id: responseData?.feedback_id || null,
          query_id: responseData?.query_id || payload.query_id,
          message_id: responseData?.message_id || payload.message_id || null,
          rating: responseData?.rating || payload.rating,
          comment: responseData?.comment || payload.comment || null,
          created_at: responseData?.created_at || new Date().toISOString(),
        },
      }));

      handleCloseFeedbackForm();
      pushFeedbackToast("success", "Feedback saved");
    } catch (err) {
      console.error("Feedback submit error:", err);
      const networkMessage =
        "Unable to save feedback right now. Please check your connection and retry.";
      setFeedbackError(networkMessage);
      pushFeedbackToast("error", networkMessage);
    } finally {
      setFeedbackSubmitPendingByMessage((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  };

  const fetchSessionHistory = async () => {
    setIsHistoryLoading(true);
    setHistoryError("");
    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/history`,
        {
          method: "GET",
          headers: { Authorization },
        },
      );
      if (!res.ok) {
        throw new Error(`Session history failed: ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);
      setSessionHistory(normalized);
      // Also update context for sidebar sync
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
      return normalized;
    } catch (err) {
      console.error("Session history error:", err);
      setSessionHistory([]);
      setHistoryError("Unable to load sessions.");
      return [];
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const loadSessionMessages = async (
    selectedSessionId,
    selectedConversationId,
  ) => {
    if (!selectedSessionId) return;

    const requestId =
      (sessionMessagesRequestIdRef.current[selectedSessionId] || 0) + 1;
    sessionMessagesRequestIdRef.current[selectedSessionId] = requestId;
    setActiveChatId(selectedSessionId);
    setActiveHistorySessionId(selectedSessionId);
    setActiveSessionIdByChatId((prev) => ({
      ...prev,
      [selectedSessionId]: selectedSessionId,
    }));
    setThreadIdByChatId((prev) => ({
      ...prev,
      [selectedSessionId]: selectedConversationId || null,
    }));
    setSessionId(selectedSessionId);
    setThreadId(selectedConversationId || null);
    setChatMessages(selectedSessionId, []);
    setShowSources(false);
    setActiveSqlQueries([]);
    setExpandedSourceItems({});
    setSessionMessagesLoadingByChatId((prev) => ({
      ...prev,
      [selectedSessionId]: true,
    }));
    setSessionMessagesErrorByChatId((prev) => ({
      ...prev,
      [selectedSessionId]: "",
    }));
    setSessionMessagesRetryableByChatId((prev) => ({
      ...prev,
      [selectedSessionId]: false,
    }));

    if (activeChatIdRef.current === selectedSessionId) {
      setIsSessionMessagesLoading(true);
      setSessionMessagesError("");
      setSessionMessagesRetryable(false);
    }

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/${selectedSessionId}/messages`,
        {
          method: "GET",
          headers: { Authorization },
        },
      );

      if (!res.ok) {
        const isRetryable = res.status === 503 || res.status === 504;
        setSessionMessagesRetryableByChatId((prev) => ({
          ...prev,
          [selectedSessionId]: isRetryable,
        }));
        if (activeChatIdRef.current === selectedSessionId) {
          setSessionMessagesRetryable(isRetryable);
        }
        throw new Error(`Session messages failed: ${res.status}`);
      }

      const data = await res.json();
      if (
        requestId !== sessionMessagesRequestIdRef.current[selectedSessionId]
      ) {
        return;
      }
      const normalizedMessages = normalizeSessionMessages(data);
      setChatMessages(selectedSessionId, normalizedMessages);
      if (activeChatIdRef.current === selectedSessionId) {
        setMessages(normalizedMessages);
      }
    } catch (err) {
      if (
        requestId !== sessionMessagesRequestIdRef.current[selectedSessionId]
      ) {
        return;
      }
      console.error("Session messages error:", err);
      setChatMessages(selectedSessionId, []);
      setSessionMessagesErrorByChatId((prev) => ({
        ...prev,
        [selectedSessionId]: "Unable to load messages. Please retry.",
      }));
      if (activeChatIdRef.current === selectedSessionId) {
        setMessages([]);
        setSessionMessagesError("Unable to load messages. Please retry.");
      }
    } finally {
      if (
        requestId === sessionMessagesRequestIdRef.current[selectedSessionId]
      ) {
        setSessionMessagesLoadingByChatId((prev) => ({
          ...prev,
          [selectedSessionId]: false,
        }));
        if (activeChatIdRef.current === selectedSessionId) {
        setIsSessionMessagesLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (showSidebar) {
      fetchSessionHistory().then((history) => {
        // Auto-select the most recent session on page load/refresh (no navigation state)
        if (
          !hasNavigationState &&
          !activeHistorySessionId &&
          !activeChatId &&
          history &&
          history.length > 0
        ) {
          loadSessionMessages(history[0].sessionId, history[0].conversationId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar, location.key]);

  useEffect(() => {
    if (sessionId) {
      setActiveHistorySessionId(sessionId);
      setActiveChatId(sessionId);
      setActiveSessionIdByChatId((prev) => ({
        ...prev,
        [sessionId]: sessionId,
      }));
    }
  }, [sessionId]);

  // Reset state on every navigation. Trigger /query only when state is explicitly passed (not page load).
  useEffect(() => {
    // Page load/refresh: location.state is null → auto-select the most recent session.
    if (!hasNavigationState) {
      return;
    }

    // Browser refresh/back-forward can preserve location.state; do not auto-submit prompts.
    if (navigationType === "POP") {
      if (initialSessionId) {
        loadSessionMessages(initialSessionId, initialThreadId);
        return;
      }
      return;
    }

    if (!initialMessage && initialSessionId) {
      loadSessionMessages(initialSessionId, initialThreadId);
      return;
    }

    if (!initialMessage) {
      return;
    }

    // Navigation from landing page with message: start a fresh real conversation.
    // Guard: only fire query if this is a NEW message (not already processed)
    if (lastProcessedMessage === initialMessage) {
      return; // Already processed this message, skip
    }

    setMessage("");

    let isCancelled = false;
    (async () => {
      try {
        let sid = initialSessionId;
        let tid = initialThreadId;

        if (!sid) {
          const session = await createSession();
          sid = session.session_id;
          tid = session.thread_id;
        }

        if (isCancelled) return;
        const chatId = sid;
        setSessionId(sid);
        setThreadId(tid);
        setActiveHistorySessionId(sid);
        setActiveChatId(chatId);
        setActiveSessionIdByChatId((prev) => ({ ...prev, [chatId]: sid }));
        setThreadIdByChatId((prev) => ({ ...prev, [chatId]: tid || null }));
        setLastProcessedMessage(initialMessage); // Mark as processed

        // Add to sidebar immediately with user message as title
        setSessionHistory((prev) => {
          if (prev.some((s) => s.sessionId === sid)) return prev;
          return [
            {
              sessionId: sid,
              conversationId: "",
              title: initialMessage,
              lastMessage: initialMessage,
              lastIntent: "",
              messageCount: 0,
              turnCount: 0,
              isPinned: false,
              pinnedAt: null,
              createdAt: new Date().toISOString(),
              lastActiveAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            ...prev,
          ];
        });

        openSSEStream(chatId, sid);
        await fireMessage(initialMessage, chatId, sid, tid);
      } catch (err) {
        if (!isCancelled) {
          console.error("Session bootstrap failed:", err);
          const targetChatId = initialSessionId || activeChatId;
          setChatMessages(targetChatId, (prev) => [
            ...prev,
            {
              type: "ai",
              response: "Something went wrong. Please try again.",
              followUps: [],
              sourcesCount: 0,
              sqlQueries: [],
            },
          ]);
          if (targetChatId && targetChatId === activeChatId) {
            setMessages((prev) => [
              ...prev,
              {
                type: "ai",
                response: "Something went wrong. Please try again.",
                followUps: [],
                sourcesCount: 0,
                sqlQueries: [],
              },
            ]);
          }
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.key,
    navigationType,
    hasNavigationState,
    initialMessage,
    initialSessionId,
    initialThreadId,
  ]);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      const abortMap = sseAbortRef.current;
      if (abortMap && typeof abortMap.forEach === "function") {
        abortMap.forEach((controller) => controller?.abort?.());
      }
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [messages, isThinking, plannerSteps]);

  useEffect(() => {
    if (!isThinking) return;
    const interval = setInterval(() => setElapsedSeconds((p) => p + 1), 1000);
    return () => clearInterval(interval);
  }, [isThinking]);

  useEffect(() => {
    const latestAiMessage = [...messages]
      .reverse()
      .find((m) => m.type === "ai");
    const latestSqlQueries = Array.isArray(latestAiMessage?.sqlQueries)
      ? latestAiMessage.sqlQueries
      : [];
    setActiveSqlQueries(latestSqlQueries);
    setExpandedSourceItems({});
  }, [messages]);

  const createSession = async () => {
    const { Authorization } = await getAuthHeader();
    const res = await fetch(`${appConstants.SA_AI_BASE_URL}/api/v1/session`, {
      method: "POST",
      headers: { Authorization, "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error(`Session creation failed: ${res.status}`);
    return res.json();
  };

  const openSSEStream = async (chatId, sid) => {
    if (!chatId || !sid) return;

    const existingController = sseAbortRef.current.get(chatId);
    if (existingController) {
      existingController.abort();
      sseAbortRef.current.delete(chatId);
    }

    const controller = new AbortController();
    sseAbortRef.current.set(chatId, controller);
    activePollByChatId.current.set(chatId, {
      type: "stream",
      sessionId: sid,
      startedAt: Date.now(),
    });
    const { Authorization } = await getAuthHeader();

    fetch(`${appConstants.SA_AI_BASE_URL}/api/v1/stream?session_id=${sid}`, {
      headers: { Authorization },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";
          for (const part of parts) {
            if (!part.trim()) continue;
            let eventName = "message";
            let dataStr = "";
            for (const line of part.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:"))
                dataStr += line.slice(5).trim();
            }
            if (!dataStr) continue;
            switch (eventName) {
              case "planner_reasoning_step": {
                try {
                  const reasoningStep = JSON.parse(dataStr);
                  setPlannerStepsByChatId((prev) => {
                    const nextSteps = applyPlannerReasoningStepUpdate(
                      prev[chatId] || [],
                      reasoningStep,
                    );
                    return {
                      ...prev,
                      [chatId]: nextSteps,
                    };
                  });
                  setPollUpdatesByChatId((prev) => {
                    const nextSteps = applyPlannerReasoningStepUpdate(
                      prev[chatId] || [],
                      reasoningStep,
                    );
                    return {
                      ...prev,
                      [chatId]: nextSteps,
                    };
                  });

                  if (activeChatIdRef.current === chatId) {
                    setPlannerSteps((prev) =>
                      applyPlannerReasoningStepUpdate(prev, reasoningStep),
                    );
                  }
                } catch (e) {
                  // Ignore malformed planner reasoning step events.
                }
                break;
              }
              case "planner_complete": {
                try {
                  const plannerData = JSON.parse(dataStr);
                  const completeSteps = plannerData.execution_plan?.steps || [];
                  setQueryIntentDoneByChatId((prev) => ({
                    ...prev,
                    [chatId]: true,
                  }));
                  setPlannerStepsByChatId((prev) => {
                    const steps = mergePlannerCompleteWithThoughts(
                      prev[chatId] || [],
                      completeSteps,
                    );
                    return {
                      ...prev,
                      [chatId]: steps,
                    };
                  });
                  setPollUpdatesByChatId((prev) => {
                    const steps = mergePlannerCompleteWithThoughts(
                      prev[chatId] || [],
                      completeSteps,
                    );
                    return {
                      ...prev,
                      [chatId]: steps,
                    };
                  });

                  if (activeChatIdRef.current === chatId) {
                    setQueryIntentDone(true);
                    setPlannerSteps((prev) =>
                      mergePlannerCompleteWithThoughts(prev, completeSteps),
                    );
                  }
                } catch (e) {
                  setQueryIntentDoneByChatId((prev) => ({
                    ...prev,
                    [chatId]: true,
                  }));
                  if (activeChatIdRef.current === chatId) {
                    setQueryIntentDone(true);
                  }
                }
                break;
              }
              default:
                break;
            }
          }
        }

        sseAbortRef.current.delete(chatId);
        activePollByChatId.current.delete(chatId);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("SSE error:", err);
        sseAbortRef.current.delete(chatId);
        activePollByChatId.current.delete(chatId);
      });
  };

  const fireMessage = async (text, chatId, sid, tid) => {
    if (!chatId) return;
    let finalStatus = "done";

    setChatMessages(chatId, (prev) => [...prev, { type: "user", text }]);
    setIsThinkingByChatId((prev) => ({ ...prev, [chatId]: true }));
    setQueryIntentDoneByChatId((prev) => ({ ...prev, [chatId]: false }));
    setPlannerStepsByChatId((prev) => ({ ...prev, [chatId]: [] }));
    setPollUpdatesByChatId((prev) => ({ ...prev, [chatId]: [] }));
    setChatLoadingStatus(chatId, "loading");

    if (activeChatIdRef.current === chatId) {
      setMessages((prev) => [...prev, { type: "user", text }]);
      setIsThinking(true);
      setQueryIntentDone(false);
      setPlannerSteps([]);
      setElapsedSeconds(0);
    }

    try {
      const { Authorization } = await getAuthHeader();
      const body = { query: text };
      if (sid) body.session_id = sid;
      if (tid) body.thread_id = tid;

      const res = await fetch(`${appConstants.SA_AI_BASE_URL}/api/v1/query`, {
        method: "POST",
        headers: { Authorization, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(`Query failed: ${res.status}`);
      const data = await res.json();
      const sqlQueries = Array.isArray(data.sql_queries)
        ? data.sql_queries
            .filter((query) => typeof query === "string" && query.trim())
            .map((query) => query.trim())
        : [];

      // Extract full Insights block (including multiline bullets) and remove it from response body.
      const { insight, responseWithoutInsights } = extractInsightsBlock(
        data.response || "",
      );

      const assistantMessage = {
        id: data.query_id || data.queryId || `assistant-${Date.now()}`,
        queryId: data.query_id || data.queryId || null,
        messageId: data.message_id || data.messageId || null,
        type: "ai",
        metadata: formatDataAsOn(data.last_refreshed),
        thoughtDurationSeconds: formatThoughtDurationSeconds(data.total_time_ms),
        insight: insight,
        response: responseWithoutInsights,
        followUps: data.follow_up_questions || [],
        sourcesCount: sqlQueries.length,
        sqlQueries,
        sections: [],
        metrics: null,
        chartConfig: data.chart_config || null,
        genieRawData: data.genie_raw_data || null,
        aiThinkingLines: buildAiThinkingLines(
          data.planner_reasoning?.reasoning_chain || [],
          pollUpdatesByChatId[chatId] || [],
        ),
      };

      setChatMessages(chatId, (prev) => [...prev, assistantMessage]);
      if (activeChatIdRef.current === chatId) {
        setMessages((prev) => [...prev, assistantMessage]);
      }

      if (sid) {
        setActiveSessionIdByChatId((prev) => ({ ...prev, [chatId]: sid }));
      }
    } catch (err) {
      console.error("Query error:", err);
      finalStatus = "error";

      const errorMessage = {
        type: "ai",
        response: "Something went wrong. Please try again.",
        followUps: [],
        sourcesCount: 0,
        sqlQueries: [],
      };

      setChatMessages(chatId, (prev) => [...prev, errorMessage]);
      if (activeChatIdRef.current === chatId) {
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      setIsThinkingByChatId((prev) => ({ ...prev, [chatId]: false }));
      setChatLoadingStatus(chatId, finalStatus);
      activePollByChatId.current.delete(chatId);

      if (activeChatIdRef.current === chatId) {
        setIsThinking(false);
      }
    }
  };

  const handleSend = async () => {
    if (!message.trim() || isActiveChatLoading) return;
    const text = message.trim();
    setMessage("");

    let chatId = activeChatId;
    let sid = chatId ? activeSessionIdByChatId[chatId] || chatId : sessionId;
    let tid = chatId ? threadIdByChatId[chatId] || null : threadId;

    if (!sid) {
      try {
        const session = await createSession();
        sid = session.session_id;
        tid = session.thread_id;
        chatId = sid;
        setActiveChatId(chatId);
        setSessionId(sid);
        setThreadId(tid);
        setActiveHistorySessionId(sid);
        setActiveSessionIdByChatId((prev) => ({ ...prev, [chatId]: sid }));
        setThreadIdByChatId((prev) => ({ ...prev, [chatId]: tid || null }));

        // Create a temporary session object for immediate sidebar display
        const tempSession = {
          sessionId: sid,
          conversationId: "",
          title: text,
          lastMessage: text,
          lastIntent: "",
          messageCount: 0,
          turnCount: 0,
          isPinned: false,
          pinnedAt: null,
          createdAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Add to local sessionHistory immediately so sidebar updates without waiting for fetch
        setSessionHistory((prev) => [tempSession, ...prev]);

        // Refetch session history to get final title and metadata from server
        fetchSessionHistory();
      } catch (err) {
        console.error("Session creation failed:", err);
        return;
      }
    }

    // Re-open stream for every query so follow-ups receive planner progress events.
    openSSEStream(chatId, sid);
    await fireMessage(text, chatId, sid, tid);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const autoResizeInput = (el) => {
    if (!el) return;
    const maxHeight = 180;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    autoResizeInput(e.target);
  };

  useEffect(() => {
    autoResizeInput(inputRef.current);
  }, [message]);

  return (
    <div className="mi-chat-container sa-chat-container sa-conv-container">
      {/* SUBHEADER */}
      <div className="mi-subheader">
        <button
          className="mi-back-btn"
          onClick={() => navigate("/spend-analytics-ai-agent")}
        >
          <Icon icon="arrow_back" />
        </button>
        <button
          className="sa-history-toggle-btn"
          onClick={() => setShowSidebar((p) => !p)}
          aria-label={
            showSidebar ? "Collapse chats panel" : "Expand chats panel"
          }
          title={showSidebar ? "Collapse chats panel" : "Expand chats panel"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1.66675 3.33398V16.6673H18.3334V3.33398H1.66675ZM16.6667 7.22565H14.5834V5.00065H16.6667V7.22565ZM14.5834 8.89232H16.6667V11.1173H14.5834V8.89232ZM3.33341 5.00065H12.9167V15.0007H3.33341V5.00065ZM14.5834 15.0007V12.7757H16.6667V15.0007H14.5834Z"
              fill="#004895"
            />
          </svg>
        </button>
        <SpendAnalyticsBadge variant="header" />
        <span className="mi-agent-name">Spend Analytics</span>
        <Icon icon="expand_more" />
      </div>

      {/* THREE-PANEL BODY */}
      <div className="sa-conv-body">
        {/* LEFT PANEL: CHATS */}
        {showSidebar && (
          <div className="mi-chat-sidebar sa-conv-left-panel">
            <div className="mi-chats-header">
              <span>Chats</span>
              <button
                className="mi-close-sidebar"
                onClick={() => setShowSidebar(false)}
              >
                <Icon icon="close" />
              </button>
            </div>
            <button
              className="mi-new-chat-btn sa-new-chat-btn"
              disabled={isRequestInFlight}
              aria-label={
                isRequestInFlight
                  ? "New chat disabled while response is loading"
                  : "Start a new chat"
              }
              onClick={() => {
                if (isRequestInFlight) return;
                navigate("/spend-analytics-ai-agent");
              }}
            >
              {isRequestInFlight ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9.99984 1.66602C5.39984 1.66602 1.6665 5.39935 1.6665 9.99935C1.6665 14.5993 5.39984 18.3327 9.99984 18.3327C14.5998 18.3327 18.3332 14.5993 18.3332 9.99935C18.3332 5.39935 14.5998 1.66602 9.99984 1.66602ZM3.33317 9.99935C3.33317 6.32435 6.32484 3.33268 9.99984 3.33268C11.5215 3.33268 12.9265 3.84102 14.0498 4.69935L4.69984 14.0493C3.8415 12.926 3.33317 11.521 3.33317 9.99935ZM9.99984 16.666C8.47817 16.666 7.07317 16.1577 5.94984 15.2993L15.2998 5.94935C16.1582 7.07268 16.6665 8.47768 16.6665 9.99935C16.6665 13.6743 13.6748 16.666 9.99984 16.666Z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M15.8334 10.8327H10.8334V15.8327H9.16675V10.8327H4.16675V9.16602H9.16675V4.16602H10.8334V9.16602H15.8334V10.8327Z"
                    fill="currentColor"
                  />
                </svg>
              )}
              <span>New chat</span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">Recents</div>
              {isHistoryLoading && (
                <div className="mi-recent-item">Loading sessions...</div>
              )}
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">{historyError}</div>
              )}
              {!isHistoryLoading &&
                !historyError &&
                sessionHistory.length === 0 && (
                  <div className="mi-recent-item">No sessions</div>
                )}
              {!isHistoryLoading &&
                !historyError &&
                sessionHistory.map((chat) => (
                  <div
                    key={chat.sessionId}
                    className={`sa-conv-recent-row${chat.sessionId === activeHistorySessionId ? " sa-conv-recent-row--active" : ""}`}
                  >
                    <button
                      className={`mi-recent-item sa-conv-recent-item${chat.sessionId === activeHistorySessionId ? " sa-conv-recent-active" : ""}`}
                      onClick={() => {
                        setActiveChatId(chat.sessionId);
                        setActiveHistorySessionId(chat.sessionId);
                        setSessionId(chat.sessionId);
                        setThreadId(chat.conversationId || null);

                        const cachedMessages = messagesByChatId[chat.sessionId];
                        if (Array.isArray(cachedMessages) && cachedMessages.length > 0) {
                          setMessages(cachedMessages);
                          return;
                        }

                        loadSessionMessages(chat.sessionId, chat.conversationId);
                      }}
                      title={chat.title}
                    >
                      <div className="sa-conv-recent-title-wrap">
                        <span className="sa-conv-recent-title">
                          {chat.title}
                        </span>
                        <span className="sa-conv-recent-time">
                          {getRelativeTime(chat.lastActiveAt)}
                        </span>
                      </div>
                      {chat.isPinned && (
                        <span className="sa-conv-recent-pinned-badge">
                          Pinned
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="sa-conv-recent-actions-trigger"
                      aria-label="Session actions"
                      title="Session actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecentMenuOpenId((prev) =>
                          prev === chat.sessionId ? null : chat.sessionId,
                        );
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 3.25C9.69036 3.25 10.25 2.69036 10.25 2C10.25 1.30964 9.69036 0.75 9 0.75C8.30964 0.75 7.75 1.30964 7.75 2C7.75 2.69036 8.30964 3.25 9 3.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 10.25C9.69036 10.25 10.25 9.69036 10.25 9C10.25 8.30964 9.69036 7.75 9 7.75C8.30964 7.75 7.75 8.30964 7.75 9C7.75 9.69036 8.30964 10.25 9 10.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 17.25C9.69036 17.25 10.25 16.6904 10.25 16C10.25 15.3096 9.69036 14.75 9 14.75C8.30964 14.75 7.75 15.3096 7.75 16C7.75 16.6904 8.30964 17.25 9 17.25Z"
                          fill="#4B4B4B"
                        />
                      </svg>
                    </button>
                    {recentMenuOpenId === chat.sessionId && (
                      <div className="sa-conv-recent-actions-menu" role="menu">
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item"
                          disabled={Boolean(pinLoadingById[chat.sessionId])}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePinSession(chat);
                          }}
                        >
                          {pinLoadingById[chat.sessionId]
                            ? "Updating..."
                            : chat.isPinned
                              ? "Unpin"
                              : "Pin"}
                        </button>
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item sa-conv-recent-actions-item--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetSessionId(chat.sessionId);
                            closeRecentMenu();
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* MIDDLE PANEL: MESSAGES */}
        <div className="sa-conv-middle-panel">
          <div className="sa-conv-messages" ref={messagesContainerRef}>
            {isSessionMessagesLoading && (
              <div className="sa-conv-message-state sa-conv-message-state--loading">
                <span className="sa-conv-spinner" aria-hidden="true" />
                Loading messages...
              </div>
            )}

            {!isSessionMessagesLoading && sessionMessagesError && (
              <div className="sa-conv-message-state sa-conv-message-state--error">
                <span>{sessionMessagesError}</span>
                {sessionMessagesRetryable && activeHistorySessionId && (
                  <button
                    type="button"
                    className="sa-conv-message-retry-btn"
                    onClick={() =>
                      loadSessionMessages(activeHistorySessionId, threadId)
                    }
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            {!isSessionMessagesLoading &&
              !sessionMessagesError &&
              activeHistorySessionId &&
              messages.length === 0 && (
                <div className="sa-conv-message-state sa-conv-message-state--empty">
                  No messages yet
                </div>
              )}

            {messages.map((msg, idx) =>
              msg.type === "user" ? (
                <div key={idx} className="sa-conv-user-msg">
                  <span className="sa-conv-user-text">{msg.text}</span>
                  <div className="sa-conv-user-avatar">
                    <Icon icon="account_circle" />
                  </div>
                </div>
              ) : (
                <AIMessage
                  key={idx}
                  msg={msg}
                  onFollowUp={(f) => {
                    setMessage(f);
                  }}
                  onShowSources={(sqlQueries) => {
                    setShowSources(true);
                    setActiveSqlQueries(
                      Array.isArray(sqlQueries) ? sqlQueries : [],
                    );
                    setExpandedSourceItems({});
                  }}
                  feedback={messageFeedback[idx]}
                  onFeedback={(feedbackType) =>
                    handleMessageFeedback(idx, feedbackType, msg)
                  }
                  messageIndex={idx}
                  feedbackFormOpen={feedbackFormOpen}
                  feedbackText={feedbackText}
                  feedbackError={feedbackFormOpen === idx ? feedbackError : ""}
                  feedbackSubmitPending={Boolean(
                    feedbackSubmitPendingByMessage[
                      getFeedbackMessageKey(idx, msg)
                    ],
                  )}
                  feedbackAlreadySubmitted={Boolean(
                    submittedFeedbackByMessage[getFeedbackMessageKey(idx, msg)],
                  )}
                  onFeedbackTextChange={setFeedbackText}
                  onFeedbackCancel={handleCloseFeedbackForm}
                  onFeedbackSubmit={handleSubmitFeedback}
                />
              ),
            )}
            {isActiveChatLoading && isThinking && (
              <ThinkingAnimation
                queryIntentDone={queryIntentDone}
                plannerSteps={plannerSteps}
              />
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* INPUT AREA */}
          <div className="sa-conv-input-area">
            <div className="mi-input-wrapper">
              <textarea
                ref={inputRef}
                rows={1}
                className="mi-input"
                placeholder="Send a message to Spend Analytics."
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                disabled={isActiveChatLoading}
              />
              <button
                className="mi-send-btn"
                onClick={handleSend}
                disabled={!message.trim() || isActiveChatLoading}
              >
                <Icon icon="send" />
              </button>
            </div>
            <p className="mi-input-disclaimer">
              By messaging the AI agent, you confirm that you have read and
              agree to our{" "}
              <a
                href="https://pepsicogsearchaiprod.blob.core.windows.net/pepgpt-files/PepsiCo_Code_of_conduct_for_PepGPT.pdf?sp=r&st=2025-03-25T06:22:44Z&se=2028-03-25T14:22:44Z&spr=https&sv=2024-11-04&sr=b&sig=Npv1BuV9QUzSaDssiOASAeQMigaf%2F7HLnsUHu2f2CxE%3D"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://www.mypepsico.com/mypepsico?id=mypepsico_kb_article_view&sysparm_article=KB0160983&table=kb_knowledge&searchTerm=PEPSICO%20EMPLOYEE%20PRIVACY"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Notice
              </a>
              . Please do not share sensitive information. Chats may be reviewed
              and used to improve our models.
            </p>
          </div>
        </div>

        {deleteTargetSessionId && (
          <div className="sa-conv-delete-modal-backdrop" role="presentation">
            <div
              className="sa-conv-delete-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Delete chat"
            >
              <h3>Delete chat?</h3>
              <p>
                Are you sure you want to delete this chat? This action cannot be
                undone.
              </p>
              <div className="sa-conv-delete-modal-actions">
                <button
                  type="button"
                  onClick={() => setDeleteTargetSessionId(null)}
                  disabled={isDeletingSession}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sa-conv-delete-confirm-btn"
                  onClick={handleConfirmDeleteSession}
                  disabled={isDeletingSession}
                >
                  {isDeletingSession ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT PANEL: SOURCES */}
        {showSources && (
          <div className="sa-conv-right-panel">
            <div className="sa-conv-sources-header">
              <span>Sources</span>
              <button
                className="sa-conv-sources-close"
                onClick={() => setShowSources(false)}
              >
                <Icon icon="close" />
              </button>
            </div>
            <div className="sa-conv-sources-list">
              {activeSqlQueries.length === 0 && (
                <div className="sa-conv-sources-empty">
                  No sources available.
                </div>
              )}
              {activeSqlQueries.map((sourceText, index) => (
                <SourceQueryItem
                  key={`${index}-${sourceText.slice(0, 40)}`}
                  sourceText={sourceText}
                  index={index}
                  isExpanded={Boolean(expandedSourceItems[index])}
                  onToggle={() =>
                    setExpandedSourceItems((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }))
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <ToastContainer
        className="sa-conv-feedback-toast-container"
        style={{
          display: "flex",
          width: "500px",
          maxWidth: "600px",
          padding:
            "var(--Primitives-Spacing-spacing-8, 8px) var(--Primitives-Spacing-spacing-16, 16px)",
          alignItems: "center",
          gap: "8px",
        }}
        data={feedbackToasts}
        position="bottom-center"
        showActionIcon={true}
        removeToast={removeFeedbackToast}
      />
    </div>
  );
};

export default SpendAnalyticsConversations;
